// stores/useAddProductWizard.ts
import { recordGenerationStat } from "@/api/accountBilling";
import { createContentJob, getContentJobSnapshot, subscribeContentJobEvents, type ContentJobEvent } from "@/api/contentGeneration/contentJobs";
import { generateImageStub } from "@/api/images/generateImageStub";
import { ImageEditFailedError } from "@/api/geniAiOrchestration";

/**
 * GC-6: достаём русский текст ошибки для реплики ассистента в чат-ленте.
 * - ImageEditFailedError (от бэка) — отдаёт уже переведённый errorText.
 * - Прочее (сеть до нашего бэка, отмена, баг JS) — generic fallback на русском.
 */
function extractAssistantErrorText(error: unknown): string {
  if (error instanceof ImageEditFailedError) return error.errorText;
  return "Не удалось сгенерировать изображение. Попробуйте ещё раз или измените запрос.";
}

import { imagesPrompts } from "@/api/prompts/images";
import { AIVariant, generateAnotherVariant, refineTextByUserPrompt } from "@/api/mockAI";
import { WbGood } from "@/api/parseWb/parseWbGood";
import { saveGoodVersion } from "@/api/saveGoodVersion";
import { patchDraftGoodMetadata, patchGoodGenerationMarker, saveGood } from "@/api/saveGood";
import { markGoodGenerationComplete } from "@/lib/goodGenerationCompletion";
import { sanitizeCharacteristics, sanitizeGoodVersionForApi, wizardCategoryForGood } from "@/lib/goodsApiPayload";
import { uploadImageToCrm } from "@/api/saveImage";
import { WbCharacteristic } from "@/api/wbApi/getGoodCharacteristics";
import { VariantItem } from "@/components/createGood/editGoodWithAI/PhotoSection.vue";
import { Good } from "@/entities/good";
import { GoodVersion, TimeRange } from "@/entities/goodVersion";
import { resolveWizardContentJobContentType, resolveWizardGoodStorageContentType } from "@/lib/wizardContentType";
import { marketplaceKeyToApi } from "@/marketplaces/marketplaceApiMapping";
import { MarketplaceKey } from "@/marketplaces/registry";
import { getMarketplacePolicy, getMarketplacePolicyJson } from "@/marketplaces/marketplacePolicies";
import { getNewId } from "@/utils/getNewId";
import { resolveImageUrl } from "@/utils/imageRef";
import { randomUUID } from "@/lib/randomUUID";
import type { AiStrategyBundle } from "@/types/improvement";
import {
  contentCreationWizardAbandonOnNavigationAway,
  contentCreationWizardAbandoned,
  contentCreationWizardAdvancedToStep2,
  contentCreationWizardOpened,
  contentCreationWizardReturnedToStep1,
  contentCreationWizardStepChanged,
  syncContentCreationWizardContext,
  markContentCreationOpenTrigger,
  updateContentCreationImprovePhase,
  wizardPhaseFromStep,
  type ContentCreationAbandonReason,
} from "@/lib/contentCreationWizardAnalytics";
import {
  clearContentCreationWizardSnapshot,
  hasStep1WizardContent,
  isRestorableWizardSnapshot,
  quickEditIdempotencyKey,
  readContentCreationWizardSnapshot,
  reconcileOrphanedPhotoQuickEdits,
  serializeWizardImage,
  writeContentCreationWizardSnapshot,
  type PendingPhotoQuickEdit,
  type PendingTextTask,
  type PersistedWizardSnapshot,
} from "@/lib/contentCreationWizardPersistence";
import {
  aiAssistentChatClosed,
  aiAssistentChatOpened,
  aiAssistentGenerationFinished,
  type AiAssistentCloseReason,
  type AiAssistentOpenSource,
} from "@/lib/aiAssistentAnalytics";
import {
  clearWizardPhotoSession,
  getWizardPhoto,
  putWizardPhoto,
  putContentJobSources,
  deleteContentJobSources,
} from "@/lib/wizardPhotoIndexedDb";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, nextTick, ref, watch } from "vue";
import { useAuthStore } from "./auth";
import { useGoodsStore } from "./goods";

export type GoodAddingWizardStep = "editGoodInfoManually" | "editGoodWithAI" | "viewGoodVersion" | "viewGoodVersionsList";

export type WbCategoryData = { name: string; rootName?: string; id: number; rootId?: number };

export type CategoryData = {
  wb?: WbCategoryData;
};

export type AiChatScope = "image" | "title" | "description";

export type ImprovePhase = "pipeline" | "editor";

export type AiChatMessage = {
  role: "user" | "assistant";
  content: string;
  imageUrl?: string;
  resultBlock?: { label: string; text: string };
};

/** Колбэк галереи версии: сохранить сгенерированное фото в `GoodVersionImagesGallery`. */
export type AiAssistentGalleryContext = {
  sourceImageUrl: string;
  sourceImageIndex: number;
  /** Снимок URL галереи на момент открытия чата — для превью выбранного кадра. */
  imageUrls: string[];
  applyResult: (hostedUrl: string) => void | Promise<void>;
};

export type AiAssistentOpenMeta = {
  openSource?: AiAssistentOpenSource;
  goodId?: string | null;
  versionId?: string | null;
  photoIndex?: number | null;
};

export const useGoodAddingWizardStore = defineStore("goodAddingWizard", () => {
  const MAX_PIPELINE_IMAGE_COUNT = 5;
  /** Максимум фото в сетке редактора ИИ (плитки). */
  const MAX_PRODUCT_GRID_IMAGES = 30;

  const open = ref(false);
  const step = ref("editGoodInfoManually" as GoodAddingWizardStep);
  const history = ref([] as GoodAddingWizardStep[]);

  const improvePhase = ref("editor" as ImprovePhase);
  /** Шаг 1: блок «Назад» в шапке модалки во время ИИ-анализа фото или импорта по ссылке. */
  const manualStepBackLocked = ref(false);
  const preparationProgress = ref(0);
  const preparationStageIndex = ref(0);
  const strategyBundles = ref([] as AiStrategyBundle[]);
  const selectedStrategyIndex = ref(0);
  /** Skip pipeline when re-entering if product fingerprint matches. */
  const deepAnalysisCacheKey = ref("");
  const pipelineInFlight = ref(false);
  /** Успешное завершение content-job (для режима «Фото» strategyBundles не заполняются — см. handleNext). */
  const lastContentPipelineSucceeded = ref(false);
  const waitingForGoodsSync = ref(false);
  let contentJobUnsubscribe: (() => void) | null = null;
  const activeContentJob = ref(null as { jobId: string; ownerId: string } | null);
  /** Юзер вышел с экрана генерации — джоба передана фоновому вотчеру, мастер её НЕ финализирует. */
  const detachRequested = ref(false);
  const pendingPhotoQuickEdits = ref([] as PendingPhotoQuickEdit[]);
  const pendingTextTasks = ref([] as PendingTextTask[]);
  const pendingRegenerateAllPhotoIds = ref([] as string[]);
  /** Шаг 1: выбранные фото при импорте по URL (до 5). */
  const manualStepUrlSelectedIds = ref([] as string[]);
  /** Шаг 1: ИИ-анализ фото не завершён — возобновить после reload. */
  const pendingPhotoAnalysis = ref(null as { sourceImageId: string } | null);
  /** Шаг 1: импорт по URL в процессе — повторить после reload. */
  const pendingUrlImport = ref(null as { url: string } | null);
  const manualStepImportUrl = ref("");
  const wizardSessionId = ref("");
  let isApplyingPersistedSnapshot = false;
  let persistWizardTimer: ReturnType<typeof setTimeout> | null = null;
  let pipelineRunPromise: Promise<void> | null = null;

  /** Исходные фото до пайплайна (загрузка / выбор на шаге 1) — для добавления в галерею редактора. */
  const pipelineSourceCatalog = ref([] as { id: string; url: string }[]);
  const deleteImageVersionModal = ref({
    open: false,
    imageId: null as string | null,
    versionId: null as string | null,
  });

  function deleteImageVersion(imageId: string, versionId: string) {
    const photo = data.value.product.images.find((p) => p.id === imageId);
    if (!photo) return;

    photo.variants = photo.variants.filter((v) => v.id !== versionId);

    // If the deleted version was selected, clear the selection
    if (photo.selectedVariantId === versionId) {
      photo.selectedVariantId = undefined;
    }

    photo.pickedVariantIndex = photo.pickedVariantIndex - 1;
    deleteImageVersionModal.value.open = false;
  }

  const aiAssistentChatModal = ref({
    open: false,
    scope: "image" as AiChatScope,
    /** Фото, с которого открыли «Своя идея» (для фокуса сетки после дубликата). */
    sourceImageId: null as string | null,
    imageId: null as string | null,
    imageIndex: null as number | null,
    versionId: null as string | null,
    /** Текущий кадр в превью чата: выбранное фото и последний результат генерации. */
    previewImageUrl: null as string | null,
    isGenerating: false,
    messages: [] as AiChatMessage[],
  });

  const aiAssistentGalleryContext = ref<AiAssistentGalleryContext | null>(null);
  /** Шаг 2 «Создание контента» — после «Своя идея» сохранять версию, как persistImages в галерее. */
  const aiAssistentProductEditorPersist = ref(false);
  const productImageIdeaSavedTick = ref(0);

  function closeAIAssistentChat(reason: AiAssistentCloseReason = "programmatic") {
    if (aiAssistentChatModal.value.open) {
      aiAssistentChatClosed(reason);
    }
    aiAssistentChatModal.value.open = false;
    aiAssistentChatModal.value.isGenerating = false;
    aiAssistentChatModal.value.previewImageUrl = null;
    aiAssistentGalleryContext.value = null;
    aiAssistentProductEditorPersist.value = false;
  }

  async function persistProductEditorImagesAfterIdea() {
    if (!aiAssistentProductEditorPersist.value) return;
    const goodId = data.value.product.goodId;
    if (!goodId) return;

    const version = await saveVersion();
    const goodsStore = useGoodsStore();
    const existingGood = goodsStore.goods.find((g) => g.id === goodId);
    if (existingGood) {
      const versionAlreadyExists = existingGood.versions.some((v) => v.id === version.id);
      const mergedVersions = versionAlreadyExists
        ? existingGood.versions.map((v) => (v.id === version.id ? version : v))
        : [...existingGood.versions, version];
      goodsStore.upsertGood({
        ...existingGood,
        versions: mergedVersions,
        images: version.media.images || existingGood.images,
      });
    }
    productImageIdeaSavedTick.value += 1;
  }

  function openAIAssistentChat(imageId: string, versionId: string, meta?: AiAssistentOpenMeta) {
    const imageObj = data.value.product.images.find((img) => img.id === imageId);
    if (!imageObj) return;
    const { url: imageUrl, variantId: effectiveVersionId } = resolveProductPhotoPreview(imageObj, versionId);
    const imageIndex = data.value.product.images.findIndex((img) => img.id === imageId);
    const initialMessage = {
      role: "assistant" as "assistant" | "user",
      content:
        "Привет! Я помогу отредактировать это изображение. Опишите, что вы хотите изменить — убрать фон, добавить текст, улучшить качество или любую другую идею.",
    };

    const apply = () => {
      aiAssistentGalleryContext.value = null;
      aiAssistentChatModal.value.open = true;
      aiAssistentChatModal.value.scope = "image";
      aiAssistentChatModal.value.sourceImageId = imageId;
      aiAssistentChatModal.value.imageId = imageId;
      aiAssistentChatModal.value.versionId = effectiveVersionId;
      aiAssistentChatModal.value.imageIndex = imageIndex;
      aiAssistentChatModal.value.previewImageUrl = imageUrl;
      aiAssistentChatModal.value.isGenerating = false;
      aiAssistentChatModal.value.messages = [initialMessage];
      aiAssistentChatOpened({
        scope: "image",
        openSource: meta?.openSource,
        photoId: imageId,
        photoIndex: imageIndex >= 0 ? imageIndex : null,
        goodId: meta?.goodId ?? (data.value.product.goodId || null),
        versionId: meta?.versionId ?? (data.value.product.versionId || null),
      });
    };

    const fromTextScope =
      aiAssistentChatModal.value.open &&
      (aiAssistentChatModal.value.scope === "title" || aiAssistentChatModal.value.scope === "description");

    if (fromTextScope) {
      closeAIAssistentChat("scope_switch");
      void nextTick(apply);
      return;
    }

    if (aiAssistentChatModal.value.open) {
      closeAIAssistentChat("programmatic");
    }

    apply();
  }

  /** «Своя идея» на шаге 2 (блок «Картинки»): правки product.images + сохранение версии после генерации. */
  function openAIAssistentChatForProductPhoto(imageId: string, versionId: string, meta?: AiAssistentOpenMeta) {
    aiAssistentGalleryContext.value = null;
    aiAssistentProductEditorPersist.value = true;
    openAIAssistentChat(imageId, versionId, meta);
  }

  /** Открыть чат по URL кадра (галерея версии / полноэкранный просмотр). При необходимости добавляет фото в product.images. */
  function openAIAssistentChatForResolvedUrl(imageUrl: string): boolean {
    const target = resolveImageUrl(imageUrl);
    if (!target) return false;

    for (const img of data.value.product.images) {
      if (!img.url) continue;
      if (resolveImageUrl(img.url) === target) {
        const pick = getProductPhotoPickVariant(img);
        openAIAssistentChat(img.id, pick?.id ?? img.id);
        return true;
      }
      for (const v of img.variants ?? []) {
        if (v.url && resolveImageUrl(v.url) === target) {
          openAIAssistentChat(img.id, v.id);
          return true;
        }
      }
    }

    const imageId = getNewId("image");
    const versionId = getNewId("image");
    data.value.product.images.push({
      id: imageId,
      url: target,
      variants: [{ id: versionId, url: target, source: "gallery" }],
      pickedVariantIndex: 0,
      selectedVariantId: versionId,
    });
    openAIAssistentChat(imageId, versionId);
    return true;
  }

  /**
   * «Своя идея» из галереи версии товара — результат уходит в applyResult, без правок wizard.product.images.
   */
  function openAIAssistentChatForGallery(
    imageUrl: string,
    applyResult: AiAssistentGalleryContext["applyResult"],
    meta?: AiAssistentOpenMeta,
    galleryImageUrls?: string[],
  ): boolean {
    const target = resolveImageUrl(imageUrl);
    if (!target) return false;

    const sourceImageIndex = meta?.photoIndex ?? 0;
    const imageUrls = (galleryImageUrls?.length ? galleryImageUrls : [imageUrl])
      .map((row) => resolveImageUrl(row))
      .filter(Boolean);
    const previewUrl = imageUrls[sourceImageIndex] ?? target;

    aiAssistentGalleryContext.value = {
      sourceImageUrl: target,
      sourceImageIndex,
      imageUrls,
      applyResult,
    };

    const initialMessage = {
      role: "assistant" as "assistant" | "user",
      content:
        "Привет! Я помогу отредактировать это изображение. Опишите, что вы хотите изменить — убрать фон, добавить текст, улучшить качество или любую другую идею.",
    };

    const apply = () => {
      aiAssistentChatModal.value.open = true;
      aiAssistentChatModal.value.scope = "image";
      aiAssistentChatModal.value.sourceImageId = null;
      aiAssistentChatModal.value.imageId = null;
      aiAssistentChatModal.value.versionId = null;
      aiAssistentChatModal.value.imageIndex = sourceImageIndex;
      aiAssistentChatModal.value.previewImageUrl = previewUrl;
      aiAssistentChatModal.value.isGenerating = false;
      aiAssistentChatModal.value.messages = [initialMessage];
      aiAssistentChatOpened({
        scope: "image",
        openSource: meta?.openSource ?? "version_gallery_menu",
        photoId: meta?.photoIndex != null ? String(meta.photoIndex) : null,
        photoIndex: sourceImageIndex,
        goodId: meta?.goodId ?? null,
        versionId: meta?.versionId ?? null,
      });
    };

    const fromTextScope =
      aiAssistentChatModal.value.open &&
      (aiAssistentChatModal.value.scope === "title" || aiAssistentChatModal.value.scope === "description");

    if (fromTextScope) {
      closeAIAssistentChat("scope_switch");
      void nextTick(apply);
      return true;
    }

    if (aiAssistentChatModal.value.open) {
      closeAIAssistentChat("programmatic");
    }

    apply();
    return true;
  }

  function openTextAiChat(kind: "title" | "description", meta?: AiAssistentOpenMeta) {
    const intro =
      kind === "title"
        ? "Привет! Я помогу отредактировать заголовок. Опишите, что убрать, добавить или изменить."
        : "Привет! Я помогу отредактировать описание. Опишите, что убрать, добавить или изменить.";

    const apply = () => {
      aiAssistentGalleryContext.value = null;
      aiAssistentChatModal.value.open = true;
      aiAssistentChatModal.value.scope = kind;
      aiAssistentChatModal.value.sourceImageId = null;
      aiAssistentChatModal.value.imageId = null;
      aiAssistentChatModal.value.versionId = null;
      aiAssistentChatModal.value.imageIndex = null;
      aiAssistentChatModal.value.previewImageUrl = null;
      aiAssistentChatModal.value.isGenerating = false;
      aiAssistentChatModal.value.messages = [{ role: "assistant", content: intro }];
      aiAssistentChatOpened({
        scope: kind,
        openSource:
          meta?.openSource ??
          (kind === "title" ? "text_title_block" : "text_description_block"),
        goodId: meta?.goodId ?? (data.value.product.goodId || null),
        versionId: meta?.versionId ?? (data.value.product.versionId || null),
      });
    };

    const fromImageScope = aiAssistentChatModal.value.open && aiAssistentChatModal.value.scope === "image";

    if (fromImageScope) {
      closeAIAssistentChat("scope_switch");
      void nextTick(apply);
      return;
    }

    if (aiAssistentChatModal.value.open) {
      closeAIAssistentChat("programmatic");
    }

    apply();
  }

  async function generateImageByIdea(text: string) {
    const galleryCtx = aiAssistentGalleryContext.value;
    if (galleryCtx) {
      aiAssistentChatModal.value.isGenerating = true;
      const imageUrl = galleryCtx.sourceImageUrl;
      aiAssistentChatModal.value.previewImageUrl = imageUrl;
      try {
        const newImage = await generateImageStub(imageUrl, text, undefined, {
          billingScope: "product_card",
        });
        const hostedImage = await uploadGeneratedImageVariant(newImage);
        await galleryCtx.applyResult(hostedImage);
        galleryCtx.sourceImageUrl = hostedImage;
        aiAssistentChatModal.value.previewImageUrl = hostedImage;
        aiAssistentChatModal.value.messages.push({
          role: "assistant",
          content: "Вот сгенерированное изображение на основе вашей идеи!",
        });
        aiAssistentGenerationFinished({
          scope: "image",
          outcome: "success",
          resultType: "image",
          promptLength: text.length,
        });
      } catch (error) {
        console.error(error);
        // GC-6: показываем причину отказа AI как реплику ассистента в чат-ленте
        // (с русским текстом из бэка). Раньше catch молча сглатывался → юзер
        // видел только бесконечный спиннер.
        aiAssistentChatModal.value.messages.push({
          role: "assistant",
          content: extractAssistantErrorText(error),
        });
        aiAssistentGenerationFinished({
          scope: "image",
          outcome: "error",
          resultType: "image",
          promptLength: text.length,
          errorKind:
            error instanceof ImageEditFailedError
              ? error.errorCode
              : error instanceof Error
                ? error.name
                : "unknown",
        });
      } finally {
        aiAssistentChatModal.value.isGenerating = false;
      }
      return;
    }

    aiAssistentChatModal.value.isGenerating = true;

    // GC-6: после предыдущего failed мы могли удалить duplicate, на который
    // указывал imageId — поэтому пробуем восстановить ссылку на оригинал
    // (sourceImageId хранится с момента открытия чата).
    const imageId = aiAssistentChatModal.value.imageId;
    const fallbackToSource =
      imageId && !data.value.product.images.find((img) => img.id === imageId)
        ? aiAssistentChatModal.value.sourceImageId
        : null;
    if (fallbackToSource) {
      aiAssistentChatModal.value.imageId = fallbackToSource;
      aiAssistentChatModal.value.imageIndex = data.value.product.images.findIndex(
        (img) => img.id === fallbackToSource,
      );
    }

    const sourceId = aiAssistentChatModal.value.imageId;
    const imageObj = sourceId ? data.value.product.images.find((img) => img.id === sourceId) : undefined;
    if (!imageObj) {
      // GC-6: пушим реплику ассистента, чтобы юзер не уставился в пустой чат.
      aiAssistentChatModal.value.messages.push({
        role: "assistant",
        content:
          "Не получилось найти исходное фото — возможно, оно было удалено. Откройте чат снова с нужной картинки.",
      });
      aiAssistentChatModal.value.isGenerating = false;
      aiAssistentGenerationFinished({
        scope: "image",
        outcome: "error",
        resultType: "image",
        promptLength: text.length,
        errorKind: "missing_image",
      });
      return;
    }

    const imageUrl = getProductPhotoBaseUrl(imageObj);

    if (!imageUrl) {
      aiAssistentChatModal.value.messages.push({
        role: "assistant",
        content: "Не удалось получить ссылку на исходное фото. Попробуйте ещё раз.",
      });
      aiAssistentChatModal.value.isGenerating = false;
      aiAssistentGenerationFinished({
        scope: "image",
        outcome: "error",
        resultType: "image",
        promptLength: text.length,
        errorKind: "missing_image_url",
      });
      return;
    }

    aiAssistentChatModal.value.previewImageUrl = imageUrl;

    const duplicateId = insertProductImageDuplicateAfter(sourceId!, imageUrl);
    const targetObj = duplicateId ? data.value.product.images.find((img) => img.id === duplicateId) : imageObj;
    if (!targetObj) {
      aiAssistentChatModal.value.messages.push({
        role: "assistant",
        content: "Не удалось подготовить место под новое фото. Попробуйте ещё раз.",
      });
      aiAssistentChatModal.value.isGenerating = false;
      aiAssistentGenerationFinished({
        scope: "image",
        outcome: "error",
        resultType: "image",
        promptLength: text.length,
        errorKind: "duplicate_insert_failed",
      });
      return;
    }

    if (duplicateId) {
      aiAssistentChatModal.value.imageId = duplicateId;
      aiAssistentChatModal.value.imageIndex = data.value.product.images.findIndex((img) => img.id === duplicateId);
    } else {
      targetObj.generating = true;
    }

    const idempotencyKey = quickEditIdempotencyKey(targetObj.id);
    registerPendingPhotoQuickEdit({
      targetImageId: targetObj.id,
      sourcePhotoId: sourceId!,
      editType: "custom",
      baseUrl: imageUrl,
      slot: duplicateId ? "duplicate" : "in-place",
      customPrompt: text,
      idempotencyKey,
    });

    try {
      const newImage = await generateImageStub(imageUrl, text, undefined, {
        billingScope: "product_card",
        idempotencyKey,
      });
      const hostedImage = await uploadGeneratedImageVariant(newImage);

      const variant: VariantItem = { id: getNewId("image"), url: hostedImage, source: `ai:custom` };
      targetObj.variants.push(variant);
      targetObj.pickedVariantIndex = targetObj.variants.length;
      targetObj.selectedVariantId = variant.id;

      if (duplicateId) {
        aiAssistentChatModal.value.imageId = duplicateId;
        aiAssistentChatModal.value.imageIndex = data.value.product.images.findIndex((img) => img.id === duplicateId);
      }
      aiAssistentChatModal.value.versionId = variant.id;
      aiAssistentChatModal.value.previewImageUrl = hostedImage;

      aiAssistentChatModal.value.messages.push({
        role: "assistant",
        content: "Вот сгенерированное изображение на основе вашей идеи!",
      });
      aiAssistentGenerationFinished({
        scope: "image",
        outcome: "success",
        resultType: "image",
        promptLength: text.length,
      });
      try {
        await persistProductEditorImagesAfterIdea();
      } catch (persistError) {
        console.error(persistError);
      }
    } catch (error) {
      console.error(error);
      // GC-6: показываем причину отказа AI как реплику ассистента в чат-ленте.
      // Дубликат-плейсхолдер удаляем тут же — без него юзер бы видел копию
      // оригинала под спиннером «зависшую» в галерее.
      aiAssistentChatModal.value.messages.push({
        role: "assistant",
        content: extractAssistantErrorText(error),
      });
      aiAssistentGenerationFinished({
        scope: "image",
        outcome: "error",
        resultType: "image",
        promptLength: text.length,
        errorKind:
          error instanceof ImageEditFailedError
            ? error.errorCode
            : error instanceof Error
              ? error.name
              : "unknown",
      });
      if (duplicateId) {
        removeProductImage(duplicateId);
        // GC-6: imageId/imageIndex могли быть подвинуты на duplicate (строки 530-535).
        // Возвращаем их на оригинал, чтобы повторный запрос в этом же чате
        // не упал в missing_image, и UI-индекс показывал реальный фото.
        const original = aiAssistentChatModal.value.sourceImageId;
        if (original) {
          aiAssistentChatModal.value.imageId = original;
          aiAssistentChatModal.value.imageIndex = data.value.product.images.findIndex(
            (img) => img.id === original,
          );
        }
      }
    } finally {
      targetObj.generating = false;
      clearPendingPhotoQuickEdit(targetObj.id);
      aiAssistentChatModal.value.isGenerating = false;
    }
  }

  const data = ref({
    marketplace: null as MarketplaceKey | null,
    method: null as "import" | "manual" | null,
    contentType: null as "photo" | "card" | "video" | null,
    productSourceType: null as "url" | "image" | null,
    generationNotes: "",
    generalCategory: "",
    product: {
      goodId: "",
      versionId: "",
      title: "",
      description: "",
      price: null as number | null,
      images: [] as { url: string; variants: VariantItem[]; pickedVariantIndex?: number; selectedVariantId?: string; generating?: boolean; id: string }[],
      videoUrl: null as string | null,
      characteristics: [] as { name: string; value: string }[],
      aiTitles: [] as AIVariant[],
      selectedAiTitleId: null as string | null,
      aiDescriptions: [] as AIVariant[],
      selectedAiDescriptionId: null as string | null,
      generatingTitle: false as boolean,
      generatingDescription: false as boolean,
      competitors: [] as WbGood[],
      savingVersion: false as boolean,
      isActive: false as boolean,
      category: null as CategoryData | null,
      brand: null as string | null,
      characteristicsSchema: null as WbCharacteristic[] | null,
      activePeriods: [] as TimeRange[],
      /** Snapshot before AI improvement (для переключателя «Оригинал»). */
      originalProductTitle: null as string | null,
      originalProductDescription: null as string | null,
    },
  });

  function pickImageVariant(photoId: string, index: number) {
    const photo = data.value.product.images.find((p) => p.id === photoId);
    if (!photo) return;

    const maxIdx = photo.variants.length;
    if (index < 0 || index > maxIdx) return;

    photo.pickedVariantIndex = index;
  }

  // Transient in-memory storage of created blob URLs (object URLs created via URL.createObjectURL)
  // We track them here so that they can be revoked when the wizard is reset or when finalizing.
  const transientBlobUrls = ref([] as string[]);

  function registerBlobUrl(url: string) {
    // console.log("Registered url");
    // console.log(data.value.product.images);
    if (!url) return;
    if (url.startsWith("blob:") && !transientBlobUrls.value.includes(url)) {
      transientBlobUrls.value.push(url);
    }
  }

  function revokeBlobUrls() {
    // console.log("Revoked urls");
    transientBlobUrls.value.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // ignore
      }
    });
    transientBlobUrls.value = [];
  }

  function createWizardSessionId(): string {
    return `ws_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  function resolvePersistUserId(): string {
    const authStore = useAuthStore();
    const fromAuth = String(authStore.cardGeniusAccountProfile?.userId ?? "").trim();
    if (fromAuth) return fromAuth;
    return String(readContentCreationWizardSnapshot()?.userId ?? "").trim();
  }

  function hasStep1ContentInMemory(): boolean {
    return hasStep1WizardContent({
      marketplace: data.value.marketplace,
      method: data.value.method,
      contentType: data.value.contentType,
      productSourceType: data.value.productSourceType,
      generationNotes: data.value.generationNotes,
      generalCategory: data.value.generalCategory,
      product: data.value.product,
    });
  }

  function isWizardUiEmpty(): boolean {
    return !hasStep1ContentInMemory() && step.value === "editGoodInfoManually";
  }

  function schedulePersistWizardState() {
    if (isApplyingPersistedSnapshot || !canPersistWizardState()) return;
    if (persistWizardTimer) clearTimeout(persistWizardTimer);
    persistWizardTimer = setTimeout(() => {
      persistWizardTimer = null;
      void persistWizardState();
    }, 120);
  }

  function buildPersistedSnapshot(): PersistedWizardSnapshot | null {
    const userId = resolvePersistUserId();
    if (!userId || !wizardSessionId.value) return null;

    return {
      version: 1,
      savedAt: Date.now(),
      userId,
      wizardSessionId: wizardSessionId.value,
      step: step.value,
      history: [...history.value],
      improvePhase: improvePhase.value,
      manualStepBackLocked: manualStepBackLocked.value,
      preparationProgress: preparationProgress.value,
      preparationStageIndex: preparationStageIndex.value,
      strategyBundles: strategyBundles.value,
      selectedStrategyIndex: selectedStrategyIndex.value,
      deepAnalysisCacheKey: deepAnalysisCacheKey.value,
      lastContentPipelineSucceeded: lastContentPipelineSucceeded.value,
      waitingForGoodsSync: waitingForGoodsSync.value,
      pipelineInFlight: pipelineInFlight.value,
      pipelineSourceCatalog: pipelineSourceCatalog.value.map((row) => ({ ...row })),
      activeContentJob: activeContentJob.value ? { ...activeContentJob.value } : null,
      pendingPhotoQuickEdits: pendingPhotoQuickEdits.value.map((edit) => ({ ...edit })),
      pendingTextTasks: pendingTextTasks.value.map((task) => ({ ...task })),
      pendingRegenerateAllPhotoIds: [...pendingRegenerateAllPhotoIds.value],
      manualStepUrlSelectedIds: [...manualStepUrlSelectedIds.value],
      pendingPhotoAnalysis: pendingPhotoAnalysis.value ? { ...pendingPhotoAnalysis.value } : null,
      pendingUrlImport: pendingUrlImport.value ? { ...pendingUrlImport.value } : null,
      manualStepImportUrl: manualStepImportUrl.value,
      data: {
        marketplace: data.value.marketplace,
        method: data.value.method,
        contentType: data.value.contentType,
        productSourceType: data.value.productSourceType,
        generationNotes: data.value.generationNotes,
        generalCategory: data.value.generalCategory,
        product: {
          ...data.value.product,
          images: data.value.product.images.map(serializeWizardImage),
          competitors: data.value.product.competitors,
          characteristics: [...data.value.product.characteristics],
          aiTitles: [...data.value.product.aiTitles],
          aiDescriptions: [...data.value.product.aiDescriptions],
          activePeriods: [...data.value.product.activePeriods],
        },
      },
    };
  }

  function canPersistWizardState(): boolean {
    if (isApplyingPersistedSnapshot) return false;
    if (open.value) return true;
    return step.value === "editGoodInfoManually" && hasStep1ContentInMemory();
  }

  async function persistWizardState(): Promise<void> {
    if (!canPersistWizardState()) return;
    const snapshot = buildPersistedSnapshot();
    if (!snapshot) return;
    writeContentCreationWizardSnapshot(snapshot);
  }

  /** Синхронная запись перед unload / hard reload. */
  function forcePersistWizardState(): void {
    if (isApplyingPersistedSnapshot) return;
    if (!open.value && !hasStep1ContentInMemory()) return;
    if (!wizardSessionId.value) {
      wizardSessionId.value = createWizardSessionId();
    }
    const snapshot = buildPersistedSnapshot();
    if (!snapshot) return;
    writeContentCreationWizardSnapshot(snapshot);
  }

  async function clearWizardPersistence(): Promise<void> {
    const sessionId = wizardSessionId.value;
    clearContentCreationWizardSnapshot();
    if (sessionId) {
      try {
        await clearWizardPhotoSession(sessionId);
      } catch (error) {
        console.error("[goodAddingWizard] failed to clear wizard photos", error);
      }
    }
  }

  async function registerWizardPhotoFile(imageId: string, file: Blob): Promise<void> {
    if (!wizardSessionId.value) return;
    try {
      await putWizardPhoto(wizardSessionId.value, imageId, file);
      schedulePersistWizardState();
    } catch (error) {
      console.error("[goodAddingWizard] failed to persist wizard photo file", error);
    }
  }

  async function getWizardPhotoBlob(imageId: string): Promise<Blob | null> {
    if (!wizardSessionId.value) return null;
    return getWizardPhoto(wizardSessionId.value, imageId).catch(() => null);
  }

  async function hydrateWizardPhotosFromIndexedDb(): Promise<void> {
    if (!wizardSessionId.value) return;
    for (const image of data.value.product.images) {
      const url = String(image.url ?? "");
      if (url && !url.startsWith("blob:") && !url.startsWith("data:")) continue;
      const blob = await getWizardPhoto(wizardSessionId.value, image.id).catch(() => null);
      if (!blob) continue;
      const objectUrl = URL.createObjectURL(blob);
      registerBlobUrl(objectUrl);
      image.url = objectUrl;
    }
  }

  function resetStaleGenerationFlags() {
    for (const img of data.value.product.images) {
      img.generating = false;
    }
    data.value.product.generatingTitle = false;
    data.value.product.generatingDescription = false;
    if (data.value.product.images.length > 0 || step.value !== "editGoodWithAI") {
      improvePhase.value = "editor";
    }
    waitingForGoodsSync.value = false;
  }

  function applyGenerationFlagsForStep2Resume(
    pendingQuickEditTargetIds: Set<string>,
    pendingRegenerateAllIds: Set<string>,
    pendingTextTitle: boolean,
    pendingTextDescription: boolean
  ) {
    for (const img of data.value.product.images) {
      img.generating = pendingQuickEditTargetIds.has(img.id) || pendingRegenerateAllIds.has(img.id);
    }
    data.value.product.generatingTitle = pendingTextTitle;
    data.value.product.generatingDescription = pendingTextDescription;
    if (data.value.product.images.length > 0) {
      improvePhase.value = "editor";
    }
  }

  function syncStep2ResumeGenerationUi() {
    const pendingQuickEditTargetIds = new Set(pendingPhotoQuickEdits.value.map((edit) => edit.targetImageId));
    const pendingRegenerateAllIds = new Set(pendingRegenerateAllPhotoIds.value);
    const pendingTextTitle = pendingTextTasks.value.some((task) => task.field === "title");
    const pendingTextDescription = pendingTextTasks.value.some((task) => task.field === "description");
    const hasResumeWork =
      pendingPhotoQuickEdits.value.length > 0 ||
      pendingTextTasks.value.length > 0 ||
      pendingRegenerateAllPhotoIds.value.length > 0;

    if (hasResumeWork) {
      applyGenerationFlagsForStep2Resume(
        pendingQuickEditTargetIds,
        pendingRegenerateAllIds,
        pendingTextTitle,
        pendingTextDescription,
      );
      return;
    }
    resetStaleGenerationFlags();
  }

  // GC-35 sentinel: zombie image.generating=true остаётся после прерванного
  // runActiveContentJob (вкладку закрыли, бэк убили, ошибка вне finally).
  // На странице товара /goods/:id это приводит к вечно-disabled кнопке AI:
  // anyPageGenerationBusy=true → regenerateAllDisabled=true → click never fires.
  // Реагируем на ЛЮБОЕ появление generating-флага: если ни одной живой работы
  // нет (нет activeContentJob, pipelineInFlight, pending*), скидываем флаги.
  // Live генерации не страдают: пока они идут, pipelineInFlight=true.
  watch(
    () => ({
      anyImg: data.value.product.images.some((img) => img.generating),
      genTitle: data.value.product.generatingTitle,
      genDesc: data.value.product.generatingDescription,
      activeJob: activeContentJob.value != null,
      flight: pipelineInFlight.value,
      pendingQuick: pendingPhotoQuickEdits.value.length,
      pendingText: pendingTextTasks.value.length,
      pendingRegen: pendingRegenerateAllPhotoIds.value.length,
    }),
    (state) => {
      if (isApplyingPersistedSnapshot) return;
      const liveWork =
        state.activeJob ||
        state.flight ||
        state.pendingQuick > 0 ||
        state.pendingText > 0 ||
        state.pendingRegen > 0;
      if (liveWork) return;
      if (!state.anyImg && !state.genTitle && !state.genDesc) return;
      resetStaleGenerationFlags();
    },
    { immediate: true, deep: true },
  );

  async function applyPersistedSnapshot(snapshot: PersistedWizardSnapshot): Promise<void> {
    isApplyingPersistedSnapshot = true;
    try {
      wizardSessionId.value = snapshot.wizardSessionId;
      step.value = snapshot.step;
      history.value = [...snapshot.history];
      improvePhase.value = snapshot.improvePhase;
      manualStepBackLocked.value =
        snapshot.pendingPhotoAnalysis || snapshot.pendingUrlImport ? false : snapshot.manualStepBackLocked;
      manualStepUrlSelectedIds.value = [...(snapshot.manualStepUrlSelectedIds ?? [])];
      pendingPhotoAnalysis.value = snapshot.pendingPhotoAnalysis ? { ...snapshot.pendingPhotoAnalysis } : null;
      pendingUrlImport.value = snapshot.pendingUrlImport ? { ...snapshot.pendingUrlImport } : null;
      manualStepImportUrl.value = snapshot.manualStepImportUrl ?? "";
      preparationProgress.value = snapshot.preparationProgress;
      preparationStageIndex.value = snapshot.preparationStageIndex;
      strategyBundles.value = snapshot.strategyBundles;
      selectedStrategyIndex.value = snapshot.selectedStrategyIndex;
      deepAnalysisCacheKey.value = snapshot.deepAnalysisCacheKey;
      lastContentPipelineSucceeded.value = snapshot.lastContentPipelineSucceeded;
      waitingForGoodsSync.value = false;
      pipelineInFlight.value = false;
      pipelineSourceCatalog.value = snapshot.pipelineSourceCatalog.map((row) => ({ ...row }));
      activeContentJob.value = snapshot.activeContentJob ? { ...snapshot.activeContentJob } : null;
      // When a content-job (card pipeline) is still in flight, its image slots are
      // marked generating:true but are OWNED by the job — they get filled from the
      // server snapshot/SSE on resume. Do NOT treat them as orphaned quick-edits:
      // that would fire a second product_image_edit generation (and a geni charge)
      // for each slot on every page refresh (BUG-001/002). Keep only the genuine
      // persisted quick-edits in that case.
      const contentJobInFlight =
        snapshot.activeContentJob != null &&
        (!snapshot.lastContentPipelineSucceeded || snapshot.pipelineInFlight);
      pendingPhotoQuickEdits.value = contentJobInFlight
        ? (snapshot.pendingPhotoQuickEdits ?? []).map((edit) => ({ ...edit }))
        : reconcileOrphanedPhotoQuickEdits(
            snapshot.data.product.images,
            (snapshot.pendingPhotoQuickEdits ?? []).map((edit) => ({ ...edit })),
          );
      if (
        pendingPhotoQuickEdits.value.length > (snapshot.pendingPhotoQuickEdits?.length ?? 0)
      ) {
        flushWizardPersistence();
      }
      pendingTextTasks.value = (snapshot.pendingTextTasks ?? []).map((task) => ({ ...task }));
      pendingRegenerateAllPhotoIds.value = [...(snapshot.pendingRegenerateAllPhotoIds ?? [])];
      const pendingQuickEditTargetIds = new Set(pendingPhotoQuickEdits.value.map((edit) => edit.targetImageId));
      const pendingRegenerateAllIds = new Set(pendingRegenerateAllPhotoIds.value);
      const pendingTextTitle = pendingTextTasks.value.some((task) => task.field === "title");
      const pendingTextDescription = pendingTextTasks.value.some((task) => task.field === "description");
      const willResumeContentJob = snapshot.activeContentJob != null;
      const willResumePipeline = snapshot.step === "editGoodWithAI" && snapshot.pipelineInFlight;
      const willResumeStep2ClientWork =
        pendingPhotoQuickEdits.value.length > 0 ||
        pendingTextTasks.value.length > 0 ||
        pendingRegenerateAllPhotoIds.value.length > 0;

      data.value = {
        marketplace: snapshot.data.marketplace,
        method: snapshot.data.method,
        contentType: snapshot.data.contentType,
        productSourceType: snapshot.data.productSourceType,
        generationNotes: snapshot.data.generationNotes,
        generalCategory: snapshot.data.generalCategory,
        product: {
          ...snapshot.data.product,
          generatingTitle: false,
          generatingDescription: false,
          images: snapshot.data.product.images.map((img) => ({
            id: img.id,
            url: img.url,
            variants: img.variants.map((v) => ({ ...v })),
            pickedVariantIndex: img.pickedVariantIndex,
            selectedVariantId: img.selectedVariantId,
            generating: false,
          })),
        },
      };
      await hydrateWizardPhotosFromIndexedDb();

      if (willResumeContentJob || willResumePipeline) {
        if (data.value.product.images.length > 0) {
          improvePhase.value = "editor";
        }
        if (pendingTextTitle) {
          data.value.product.generatingTitle = true;
        }
        if (pendingTextDescription) {
          data.value.product.generatingDescription = true;
        }
        for (const img of data.value.product.images) {
          if ((img.variants?.length ?? 0) > 0) continue;
          if (pendingQuickEditTargetIds.has(img.id) || pendingRegenerateAllIds.has(img.id)) {
            img.generating = true;
          }
        }
      } else if (willResumeStep2ClientWork) {
        applyGenerationFlagsForStep2Resume(
          pendingQuickEditTargetIds,
          pendingRegenerateAllIds,
          pendingTextTitle,
          pendingTextDescription
        );
      } else {
        resetStaleGenerationFlags();
      }
    } finally {
      isApplyingPersistedSnapshot = false;
    }
  }

  function flushWizardPersistence(): void {
    if (persistWizardTimer) {
      clearTimeout(persistWizardTimer);
      persistWizardTimer = null;
    }
    void persistWizardState();
  }

  function setPendingPhotoAnalysis(sourceImageId: string) {
    pendingPhotoAnalysis.value = { sourceImageId };
    schedulePersistWizardState();
  }

  function clearPendingPhotoAnalysis() {
    if (!pendingPhotoAnalysis.value) return;
    pendingPhotoAnalysis.value = null;
    schedulePersistWizardState();
  }

  function setPendingUrlImport(url: string) {
    const trimmed = url.trim();
    if (!trimmed) return;
    pendingUrlImport.value = { url: trimmed };
    manualStepImportUrl.value = trimmed;
    schedulePersistWizardState();
  }

  function clearPendingUrlImport() {
    if (!pendingUrlImport.value) return;
    pendingUrlImport.value = null;
    schedulePersistWizardState();
  }

  function setManualStepImportUrl(url: string) {
    manualStepImportUrl.value = url;
    schedulePersistWizardState();
  }

  function clearManualStepDraftExtras() {
    manualStepUrlSelectedIds.value = [];
    pendingPhotoAnalysis.value = null;
    pendingUrlImport.value = null;
    manualStepImportUrl.value = "";
  }

  async function restoreContentCreationWizard(): Promise<boolean> {
    const authStore = useAuthStore();
    const userId = String(authStore.cardGeniusAccountProfile?.userId ?? "");
    if (!userId) return false;

    const snapshot = readContentCreationWizardSnapshot();
    if (!snapshot || snapshot.userId !== userId || !isRestorableWizardSnapshot(snapshot)) return false;

    markContentCreationOpenTrigger("session_restore");
    await applyPersistedSnapshot(snapshot);
    open.value = true;

    const needsContentJobResume =
      snapshot.activeContentJob != null &&
      (!snapshot.lastContentPipelineSucceeded || snapshot.pipelineInFlight);
    const needsFullPipelineResume = snapshot.step === "editGoodWithAI" && snapshot.pipelineInFlight;
    // После applyPersistedSnapshot очередь может быть дополнена reconcileOrphanedPhotoQuickEdits —
    // смотрим актуальный store, а не только snapshot.pendingPhotoQuickEdits.
    const needsStep2ClientWorkResume =
      pendingPhotoQuickEdits.value.length > 0 ||
      pendingTextTasks.value.length > 0 ||
      pendingRegenerateAllPhotoIds.value.length > 0;

    try {
      if (needsStep2ClientWorkResume) {
        await resumePendingStep2Work();
      }
      if (needsContentJobResume || needsFullPipelineResume) {
        await resumeContentCreationAfterReload();
      }
    } finally {
      syncStep2ResumeGenerationUi();
      schedulePersistWizardState();
    }

    return true;
  }

  function registerPendingPhotoQuickEdit(edit: PendingPhotoQuickEdit) {
    pendingPhotoQuickEdits.value = pendingPhotoQuickEdits.value.filter((row) => row.targetImageId !== edit.targetImageId);
    pendingPhotoQuickEdits.value.push({ ...edit });
    flushWizardPersistence();
  }

  function photoQuickEditResultAlreadyApplied(
    target: { variants: VariantItem[] },
    edit: PendingPhotoQuickEdit,
  ): boolean {
    const expectedSource = edit.editType === "custom" ? "ai:custom" : `ai:${edit.editType}`;
    return target.variants.some((variant) => variant.source === expectedSource && Boolean(String(variant.url ?? "").trim()));
  }

  function clearPendingPhotoQuickEdit(targetImageId: string) {
    const next = pendingPhotoQuickEdits.value.filter((row) => row.targetImageId !== targetImageId);
    if (next.length === pendingPhotoQuickEdits.value.length) return;
    pendingPhotoQuickEdits.value = next;
    schedulePersistWizardState();
  }

  async function executePhotoQuickEdit(
    edit: PendingPhotoQuickEdit,
  ): Promise<{ error?: { errorText: string; errorCode: string } }> {
    const target = data.value.product.images.find((img) => img.id === edit.targetImageId);
    if (!target) {
      clearPendingPhotoQuickEdit(edit.targetImageId);
      return {};
    }

    // GC-35: убрали early return по photoQuickEditResultAlreadyApplied — он
    // блокировал повторные клики AI на карточке, где уже есть ai:<editType>
    // variant. От двойной генерации защищает бэк через Idempotency-Key:
    // repeated POST с тем же ключом отдаёт cached результат мгновенно, geni
    // не списывается дважды.

    // GC-35: НЕЛЬЗЯ ставить has-проверку — runProductPhotoQuickEdit при
    // slot=in-place берёт lock на photoId, который равен edit.targetImageId,
    // и executePhotoQuickEdit (вызванный сразу после) видел его как «уже
    // занят» → молча выходил. Гонка от resumePendingStep2Work не страшна:
    // Set.add() идемпотентен; finally ниже снимет lock в любом случае.
    photoQuickEditLocks.add(edit.targetImageId);

    target.generating = true;
    let succeeded = false;
    // GC-6: errorInfo выносим наружу, чтобы runProductPhotoQuickEdit мог
    // передать в UI (InlineErrorBanner) русский текст причины из бэка.
    let errorInfo: { errorText: string; errorCode: string } | undefined;
    try {
      const prompt =
        edit.customPrompt?.trim() ||
        imagesPrompts[edit.editType as keyof typeof imagesPrompts] ||
        imagesPrompts.enhanceQuality;
      const idempotencyKey = edit.idempotencyKey ?? randomUUID();
      const generatedImage = await generateImageStub(edit.baseUrl, prompt, undefined, {
        billingScope: "product_card",
        idempotencyKey,
      });
      const hostedImage = await uploadGeneratedImageVariant(generatedImage);
      const variant: VariantItem = {
        id: getNewId("image"),
        url: hostedImage,
        source: `ai:${edit.editType}`,
      };
      target.variants.push(variant);
      target.pickedVariantIndex = target.variants.length;
      target.selectedVariantId = variant.id;
      succeeded = true;
    } catch (error) {
      console.error("[executePhotoQuickEdit]", error);
      // GC-6: ImageEditFailedError несёт русский errorText от бэка.
      // Любую другую ошибку (сеть до нашего бэка, аборт) превращаем в
      // generic-сообщение чтобы UI не оставался молчаливым.
      if (error instanceof ImageEditFailedError) {
        errorInfo = { errorText: error.errorText, errorCode: error.errorCode };
      } else {
        const fallbackText =
          error instanceof Error && error.message?.trim()
            ? error.message
            : "Не удалось сгенерировать изображение. Попробуйте ещё раз.";
        errorInfo = { errorText: fallbackText, errorCode: "UNKNOWN" };
      }
      // Оставляем дубликат и pending — после F5 resumePendingStep2Work повторит попытку.
    } finally {
      target.generating = false;
      photoQuickEditLocks.delete(edit.targetImageId);
      if (succeeded) {
        clearPendingPhotoQuickEdit(edit.targetImageId);
      }
      flushWizardPersistence();
    }
    return errorInfo ? { error: errorInfo } : {};
  }

  const photoQuickEditLocks = new Set<string>();

  /**
   * Быстрая AI-команда на шаге 2: дубликат слота + генерация + persist для resume после F5.
   */
  async function runProductPhotoQuickEdit(
    photoId: string,
    editType: string,
    opts?: { slot?: "duplicate" | "in-place"; customPrompt?: string; baseUrl?: string },
  ): Promise<{
    outcome: "success" | "skipped" | "error";
    duplicateId: string | null;
    // GC-6: при outcome="error" — русский текст причины из бэка + код, чтобы
    // вызывающий PhotoSection.vue положил это в InlineErrorBanner.
    error?: { errorText: string; errorCode: string };
  }> {
    if (photoQuickEditLocks.has(photoId)) return { outcome: "skipped", duplicateId: null };

    const slotMode = opts?.slot ?? "duplicate";
    const sourceImage = data.value.product.images.find((p) => p.id === photoId);
    if (!sourceImage) return { outcome: "skipped", duplicateId: null };
    // GC-35: раньше тут стоял `&& sourceImage.generating` skip, но
    // registerPendingRegenerateAll сам выставляет generating=true заранее
    // (для UI-спиннеров на всех картинках до прихода своей очереди), и check
    // ВСЕГДА срабатывал → "regenerate all" молча пропускал всё. Гонок не
    // создаём: photoQuickEditLocks выше уже отсекает повторный запуск на
    // тот же photoId.

    const mergedVariants = [{ id: sourceImage.id, url: sourceImage.url } as VariantItem].concat(sourceImage.variants ?? []);
    const baseUrl = resolveImageUrl(
      opts?.baseUrl ?? mergedVariants[sourceImage.pickedVariantIndex ?? 0]?.url ?? sourceImage.url,
    );

    let targetImage = sourceImage;
    let duplicateId: string | null = null;

    photoQuickEditLocks.add(photoId);
    try {
      if (slotMode === "duplicate") {
        duplicateId = insertProductImageDuplicateAfter(photoId, baseUrl);
        if (duplicateId) {
          const inserted = data.value.product.images.find((p) => p.id === duplicateId);
          if (inserted) targetImage = inserted;
        } else {
          sourceImage.generating = true;
          targetImage = sourceImage;
        }
      } else {
        sourceImage.generating = true;
        targetImage = sourceImage;
      }

      const pendingEdit: PendingPhotoQuickEdit = {
        targetImageId: targetImage.id,
        sourcePhotoId: photoId,
        editType,
        baseUrl,
        slot: duplicateId ? "duplicate" : slotMode,
        customPrompt: opts?.customPrompt,
        // GC-35: ключ привязан к target.id → каждый повторный клик AI шёл в
        // backend cache hit и возвращал ту же URL (видимость новой генерации
        // без реальной). Делаем уникальный per-click ключ; F5-resume
        // прочитает его из pendingEdit и повторит запрос с тем же ключом —
        // если бэк успел закэшировать результат, повторного списания geni не
        // будет.
        idempotencyKey: `quickedit:${targetImage.id}:${randomUUID()}`,
      };
      registerPendingPhotoQuickEdit(pendingEdit);

      // GC-35: НЕ выходить раньше из-за уже существующего ai:<editType>
      // variant — этот check годится для resumePendingStep2Work (где он есть
      // в executePhotoQuickEdit и пропускает дубль-запрос после F5), но для
      // нового клика юзера он означает "уже жал AI здесь — больше нельзя",
      // и кнопка молча не работает. Каждый клик AI должен создавать новый
      // variant.

      const execResult = await executePhotoQuickEdit(pendingEdit);
      if (photoQuickEditResultAlreadyApplied(targetImage, pendingEdit)) {
        return { outcome: "success", duplicateId };
      }
      // GC-6: пробрасываем структурированную ошибку наверх в PhotoSection.
      return { outcome: "error", duplicateId, error: execResult.error };
    } finally {
      photoQuickEditLocks.delete(photoId);
    }
  }

  function registerPendingTextTask(task: PendingTextTask) {
    pendingTextTasks.value = pendingTextTasks.value.filter((row) => row.taskId !== task.taskId);
    pendingTextTasks.value.push({ ...task });
    void persistWizardState();
  }

  function clearPendingTextTask(taskId: string) {
    const next = pendingTextTasks.value.filter((row) => row.taskId !== taskId);
    if (next.length === pendingTextTasks.value.length) return;
    pendingTextTasks.value = next;
    schedulePersistWizardState();
  }

  function registerPendingRegenerateAll(photoIds: string[]) {
    pendingRegenerateAllPhotoIds.value = [...photoIds];
    for (const id of photoIds) {
      const img = data.value.product.images.find((row) => row.id === id);
      if (img) img.generating = true;
    }
    void persistWizardState();
  }

  function removePendingRegenerateAllPhoto(photoId: string) {
    pendingRegenerateAllPhotoIds.value = pendingRegenerateAllPhotoIds.value.filter((id) => id !== photoId);
    schedulePersistWizardState();
  }

  function clearPendingRegenerateAll() {
    if (pendingRegenerateAllPhotoIds.value.length === 0) return;
    pendingRegenerateAllPhotoIds.value = [];
    schedulePersistWizardState();
  }

  async function executePendingTextTask(task: PendingTextTask): Promise<void> {
    const p = data.value.product;
    if (task.field === "title") {
      p.generatingTitle = true;
    } else {
      p.generatingDescription = true;
    }
    try {
      const idx = task.strategyIndex;
      if (task.mode === "variant") {
        const variant = await generateAnotherVariant(
          task.field,
          p.title,
          p.description,
          p.characteristics,
          p.competitors,
          marketplaceTextContext()
        );
        if (task.field === "title") {
          p.aiTitles[idx] = variant;
          p.title = variant.text;
          if (strategyBundles.value[idx]) {
            strategyBundles.value[idx] = { ...strategyBundles.value[idx], title: variant.text };
          }
        } else {
          p.aiDescriptions[idx] = variant;
          p.description = variant.text;
          if (strategyBundles.value[idx]) {
            strategyBundles.value[idx] = { ...strategyBundles.value[idx], description: variant.text };
          }
        }
        return;
      }

      const currentTitle = p.aiTitles[idx]?.text ?? p.title;
      const currentDesc = p.aiDescriptions[idx]?.text ?? p.description;
      const next = await refineTextByUserPrompt({
        kind: task.field,
        currentText: task.field === "title" ? currentTitle : currentDesc,
        productTitle: p.title,
        productDescription: p.description,
        userPrompt: task.userText,
        characteristics: p.characteristics,
        marketplaceContext: marketplaceTextContext(),
      });
      if (task.field === "title") {
        const id = p.aiTitles[idx]?.id ?? getNewId("title");
        p.aiTitles[idx] = { id, text: next, source: "ai" };
        p.title = next;
        if (strategyBundles.value[idx]) {
          strategyBundles.value[idx] = { ...strategyBundles.value[idx], title: next };
        }
      } else {
        const id = p.aiDescriptions[idx]?.id ?? getNewId("desc");
        p.aiDescriptions[idx] = { id, text: next, source: "ai" };
        p.description = next;
        if (strategyBundles.value[idx]) {
          strategyBundles.value[idx] = { ...strategyBundles.value[idx], description: next };
        }
      }
    } catch (error) {
      console.error("[executePendingTextTask]", error);
    } finally {
      if (task.field === "title") {
        p.generatingTitle = false;
      } else {
        p.generatingDescription = false;
      }
      clearPendingTextTask(task.taskId);
    }
  }

  function getProductPhotoPickVariant(image: (typeof data.value.product.images)[number]): VariantItem {
    const variants = [{ id: image.id, url: image.url } as VariantItem].concat(image.variants || []);
    return variants[image.pickedVariantIndex ?? 0] ?? variants[0];
  }

  function resolveProductPhotoPreview(
    image: (typeof data.value.product.images)[number],
    versionId?: string | null,
  ): { url: string; variantId: string } {
    const variants = [{ id: image.id, url: image.url } as VariantItem].concat(image.variants || []);
    const byVersion = versionId ? variants.find((row) => row.id === versionId) : undefined;
    const picked = byVersion ?? variants[image.pickedVariantIndex ?? 0] ?? variants[0];
    return {
      url: resolveImageUrl(picked?.url ?? image.url),
      variantId: picked?.id ?? image.id,
    };
  }

  function getProductPhotoBaseUrl(image: (typeof data.value.product.images)[number]): string {
    return resolveProductPhotoPreview(image).url;
  }

  function getAiAssistentChatLivePreviewUrl(): string | null {
    if (aiAssistentChatModal.value.scope !== "image") return null;
    const preview = aiAssistentChatModal.value.previewImageUrl;
    if (preview) return resolveImageUrl(preview);
    const galleryCtx = aiAssistentGalleryContext.value;
    const idx = aiAssistentChatModal.value.imageIndex ?? galleryCtx?.sourceImageIndex ?? null;
    if (galleryCtx && idx != null && idx >= 0 && idx < galleryCtx.imageUrls.length) {
      const row = galleryCtx.imageUrls[idx];
      if (row) return resolveImageUrl(row);
    }
    return null;
  }

  async function executePendingRegenerateAllPhoto(photoId: string): Promise<void> {
    const sourceImage = data.value.product.images.find((row) => row.id === photoId);
    if (!sourceImage) {
      removePendingRegenerateAllPhoto(photoId);
      return;
    }
    const baseUrl = getProductPhotoBaseUrl(sourceImage);
    await executePhotoQuickEdit({
      targetImageId: photoId,
      sourcePhotoId: photoId,
      editType: "enhanceQuality",
      baseUrl,
      slot: "in-place",
    });
    removePendingRegenerateAllPhoto(photoId);
  }

  async function resumePendingStep2Work(): Promise<void> {
    if (pipelineRunPromise) {
      await pipelineRunPromise;
    }
    try {
      const photoEdits = [...pendingPhotoQuickEdits.value];
      for (const edit of photoEdits) {
        const target = data.value.product.images.find((img) => img.id === edit.targetImageId);
        if (target && photoQuickEditResultAlreadyApplied(target, edit)) {
          clearPendingPhotoQuickEdit(edit.targetImageId);
          continue;
        }
        await executePhotoQuickEdit(edit);
      }
      const textTasks = [...pendingTextTasks.value];
      for (const task of textTasks) {
        await executePendingTextTask(task);
      }
      const regenerateIds = [...pendingRegenerateAllPhotoIds.value];
      for (const photoId of regenerateIds) {
        await executePendingRegenerateAllPhoto(photoId);
      }
    } finally {
      clearPendingRegenerateAll();
      syncStep2ResumeGenerationUi();
    }
  }

  async function replayTerminalContentJobFromQuery(jobId: string, ownerId: string): Promise<boolean> {
    const snapshot = await getContentJobSnapshot(ownerId, jobId);
    if (snapshot.status !== "completed" && snapshot.status !== "failed") {
      return false;
    }

    const p0 = data.value.product;
    const isImagesOnlyFlow = data.value.contentType === "photo";
    const handleEvent = (event: ContentJobEvent) => {
      if (event.type === "content.job.title.generated" && !isImagesOnlyFlow) {
        const nextTitle = event.payload.title;
        if (typeof nextTitle === "string" && nextTitle.trim()) {
          p0.aiTitles = [{ id: getNewId("title"), text: nextTitle, source: "ai" }];
          p0.title = nextTitle;
          p0.selectedAiTitleId = p0.aiTitles[0].id;
          p0.generatingTitle = false;
        }
        return;
      }
      if (event.type === "content.job.description.generated" && !isImagesOnlyFlow) {
        const nextDescription = event.payload.description;
        if (typeof nextDescription === "string" && nextDescription.trim()) {
          p0.aiDescriptions = [{ id: getNewId("desc"), text: nextDescription, source: "ai" }];
          p0.description = nextDescription;
          p0.selectedAiDescriptionId = p0.aiDescriptions[0].id;
          p0.generatingDescription = false;
        }
        return;
      }
      if (event.type === "content.job.image.generated") {
        const rawSlotIndex = Number(event.payload.slotIndex ?? 0);
        const imageUrl = event.payload.url;
        if (typeof imageUrl !== "string" || !imageUrl) return;
        // GC-36: см. комментарий в runActiveContentJob — оригиналы остаются в начале p0.images,
        // AI-слоты идут после. При resume после F5 эта же логика применяется к восстановленному state.
        const originalsCount = p0.images.filter((img) => img.variants?.[0]?.source === "user").length;
        const slotIndex = rawSlotIndex + originalsCount;
        const pendingQuickTargets = new Set(pendingPhotoQuickEdits.value.map((edit) => edit.targetImageId));
        while (p0.images.length <= slotIndex) {
          p0.images.push({ id: getNewId("image"), url: "", variants: [] });
        }
        const target = p0.images[slotIndex];
        if (pendingQuickTargets.has(target.id)) return;
        const variantId = event.payload.variantId || getNewId("image");
        const pipelineSourceImages =
          pipelineSourceCatalog.value.length > 0
            ? pipelineSourceCatalog.value
            : p0.images.map((img) => ({
                id: img.id,
                url: resolveImageUrl(String(img.url ?? "")),
              }));
        const source = pipelineSourceImages[rawSlotIndex % Math.max(1, pipelineSourceImages.length)];
        target.url = source?.url ?? target.url;
        // GC-36: дополняем variants, не перезаписываем.
        target.variants.push({ id: variantId, url: imageUrl, source: "ai:pipeline" });
        // GC-59: pickedVariantIndex учитывает префикс [image.url, ...variants] в tileVariants.
        target.pickedVariantIndex = target.variants.length;
        target.selectedVariantId = variantId;
        target.generating = false;
      }
    };

    handleEventFromSnapshot(snapshot, jobId, handleEvent, isImagesOnlyFlow);
    if (snapshot.status === "completed" && !lastContentPipelineSucceeded.value) {
      await finalizeContentPipelineSuccess(isImagesOnlyFlow, p0);
    } else {
      improvePhase.value = "editor";
      preparationProgress.value = 100;
      p0.generatingTitle = false;
      p0.generatingDescription = false;
      for (const ph of p0.images) {
        ph.generating = false;
      }
    }
    activeContentJob.value = null;
    pipelineInFlight.value = false;
    flushWizardPersistence();
    return true;
  }

  function reconcileEditorGenerationUi() {
    const hasActiveResumeWork =
      pendingPhotoQuickEdits.value.length > 0 ||
      pendingTextTasks.value.length > 0 ||
      pendingRegenerateAllPhotoIds.value.length > 0 ||
      activeContentJob.value != null ||
      pipelineInFlight.value;

    if (hasActiveResumeWork) return;

    const stuckFooterBlock =
      improvePhase.value === "pipeline" ||
      waitingForGoodsSync.value ||
      data.value.product.generatingTitle ||
      data.value.product.generatingDescription ||
      data.value.product.images.some((img) => img.generating);

    if (!stuckFooterBlock) return;

    resetStaleGenerationFlags();
    schedulePersistWizardState();
  }

  async function resumeContentCreationAfterReload(): Promise<void> {
    if (pipelineRunPromise) {
      await pipelineRunPromise;
      return;
    }
    if (activeContentJob.value) {
      pipelineRunPromise = runActiveContentJob();
    } else if (step.value === "editGoodWithAI") {
      pipelineRunPromise = startImprovementPipeline();
    }
    try {
      await pipelineRunPromise;
    } finally {
      pipelineRunPromise = null;
    }
  }

  const titles = computed(() => {
    return {
      editGoodInfoManually: { title: "Создание контента", subtitle: "" },
      editGoodWithAI: { title: "Создание контента", subtitle: "" },
      viewGoodVersion: { title: "", subtitle: "" },
      viewGoodVersionsList: { title: "", subtitle: "" },
    }[step.value];
  });

  function go(newStep: GoodAddingWizardStep) {
    const fromStep = step.value;
    if (step.value === "editGoodInfoManually") {
      manualStepBackLocked.value = false;
    }
    history.value.push(step.value);
    step.value = newStep;

    if (fromStep === "editGoodInfoManually" && newStep === "editGoodWithAI") {
      contentCreationWizardAdvancedToStep2();
    } else {
      contentCreationWizardStepChanged({ wizardStep: newStep, improvePhase: improvePhase.value });
    }
  }

  function enter(newStep: GoodAddingWizardStep, opts?: { resetHistory?: boolean }) {
    if (opts?.resetHistory) {
      history.value = [];
    }
    if (step.value === "editGoodInfoManually" && newStep !== "editGoodInfoManually") {
      manualStepBackLocked.value = false;
    }

    step.value = newStep;
    contentCreationWizardStepChanged({ wizardStep: newStep, improvePhase: improvePhase.value });
  }

  function back(reason?: ContentCreationAbandonReason) {
    const currentStep = step.value;
    const prev = history.value.pop();
    if (prev) {
      if (currentStep === "editGoodWithAI" && prev === "editGoodInfoManually") {
        contentCreationWizardReturnedToStep1();
      }
      step.value = prev;
      contentCreationWizardStepChanged({ wizardStep: prev, improvePhase: improvePhase.value });
      return;
    }

    contentCreationWizardAbandoned(reason ?? "header_back");
    void clearWizardPersistence();
    open.value = false;
  }

  function computeProductKey(): string {
    const p = data.value.product;
    const urls = p.images.map((i) => i.url).join("|");
    return `${p.goodId}|${p.title}|${urls}`;
  }

  function shouldSkipPipeline(): boolean {
    return (
      data.value.product.competitors.length > 0 &&
      strategyBundles.value.length > 0 &&
      deepAnalysisCacheKey.value !== "" &&
      deepAnalysisCacheKey.value === computeProductKey()
    );
  }

  function applyStrategyFromIndex(i: number) {
    const b = strategyBundles.value[i];
    const p = data.value.product;
    if (b) {
      p.title = b.title;
      p.description = b.description;
    } else if (p.aiTitles[i] && p.aiDescriptions[i]) {
      p.title = p.aiTitles[i].text;
      p.description = p.aiDescriptions[i].text;
    }
    p.selectedAiTitleId = p.aiTitles[i]?.id ?? null;
    p.selectedAiDescriptionId = p.aiDescriptions[i]?.id ?? null;
  }

  function selectStrategy(index: number) {
    if (index < 0 || index >= strategyBundles.value.length) return;
    selectedStrategyIndex.value = index;
    applyStrategyFromIndex(index);
  }

  function clearImprovementSession() {
    strategyBundles.value = [];
    lastContentPipelineSucceeded.value = false;
    deepAnalysisCacheKey.value = "";
    selectedStrategyIndex.value = 0;
    improvePhase.value = "editor";
    preparationProgress.value = 0;
    preparationStageIndex.value = 0;
  }

  function resolveWizardMarketplace(): MarketplaceKey {
    const authStore = useAuthStore();
    const m = data.value.marketplace ?? authStore.preferredMarketplace ?? "wb";
    return m as MarketplaceKey;
  }

  function marketplaceTextContext() {
    const mp = resolveWizardMarketplace();
    const policy = getMarketplacePolicy(mp);
    return {
      marketplaceDisplayName: policy.displayName,
      policyJson: getMarketplacePolicyJson(mp),
    };
  }

  /** Заполняет пустые обязательные поля из данных шага 1 (название, категория, пожелания). */
  function ensureWizardStep1ProductFields(): void {
    const authStore = useAuthStore();
    const p = data.value.product;

    if (!data.value.marketplace) {
      data.value.marketplace = (authStore.preferredMarketplace ?? "wb") as MarketplaceKey;
    }

    const categoryLabel = String(data.value.generalCategory ?? "").trim();
    const notes = String(data.value.generationNotes ?? "").trim();

    if (!String(p.title ?? "").trim()) {
      p.title = categoryLabel || "Товар";
    }

    if (!String(p.description ?? "").trim()) {
      p.description = notes || String(p.title ?? "").trim() || categoryLabel || "Описание будет сгенерировано автоматически";
    }

    if (data.value.contentType === "photo") {
      p.characteristics = p.characteristics.filter(
        (item) => item.name !== "Тип контента" && (item as { key?: string }).key !== "Тип контента"
      );
    } else if (data.value.contentType === "card") {
      const hasContentType = p.characteristics.some(
        (item) => item.name === "Тип контента" || (item as { key?: string }).key === "Тип контента"
      );
      if (!hasContentType) {
        p.characteristics.push({ name: "Тип контента", value: "Карточка" });
      }
    }
  }

  function syncWizardDraftInGoodsStore(goodId: string): void {
    const goodsStore = useGoodsStore();
    const p = data.value.product;
    const existing = goodsStore.goods.find((g) => g.id === goodId);
    const storageContentType = resolveWizardGoodStorageContentType(data.value.contentType, existing?.contentType);
    const imageUrls = p.images.map((img) => resolveImageUrl(String(img.url ?? ""))).filter(Boolean);
    const originalTitle = p.originalProductTitle ?? p.title;
    const job = activeContentJob.value;

    // Пока идёт генерация, в pinia нужна хотя бы синтетическая версия-черновик (бэкенд
    // её уже сохранил в `ensureDraftGoodSaved`): иначе карточка рендерится как пустой
    // «Товар» с дефолтным изображением и `openGood`/`activeVersion` возвращают null.
    const seededVersions =
      existing?.versions?.length
        ? existing.versions
        : [
            {
              id: p.versionId || getNewId("version"),
              title: originalTitle,
              description: p.description,
              media: { images: imageUrls },
              activePeriods: [],
              isActive: false,
              contentType: storageContentType,
            },
          ];

    goodsStore.upsertGood({
      id: goodId,
      title: originalTitle || existing?.title,
      marketplace: (data.value.marketplace ?? existing?.marketplace ?? "wb") as MarketplaceKey,
      contentType: storageContentType,
      versions: seededVersions,
      characteristics: p.characteristics.length ? p.characteristics : (existing?.characteristics ?? []),
      publishStatus: existing?.publishStatus ?? "draft",
      competitors: p.competitors.length ? p.competitors : (existing?.competitors ?? []),
      images: imageUrls.length ? imageUrls : (existing?.images ?? []),
      category: data.value.product.category ?? existing?.category ?? {},
      brand: p.brand ?? existing?.brand ?? "",
      publicationData: existing?.publicationData ?? { wb: {} },
      profile_id: existing?.profile_id,
      publicationErrors: existing?.publicationErrors,
      recommendations: existing?.recommendations,
      generating: job != null ? true : existing?.generating,
      contentJobId: job?.jobId ?? existing?.contentJobId,
      contentJobOwnerId: job?.ownerId ?? existing?.contentJobOwnerId,
      generationContext:
        job != null
          ? {
              contentType: data.value.contentType ?? undefined,
              generationNotes: data.value.generationNotes,
              generalCategory: data.value.generalCategory,
            }
          : existing?.generationContext,
    });
  }

  /** Создаёт или обновляет черновик товара с корректным `contentType` до запуска генерации. */
  async function ensureDraftGoodSaved(): Promise<string> {
    ensureWizardStep1ProductFields();
    const p = data.value.product;
    const marketplace = resolveWizardMarketplace();
    const storageContentType = resolveWizardGoodStorageContentType(data.value.contentType);
    const imageUrls = p.images.map((img) => resolveImageUrl(String(img.url ?? ""))).filter(Boolean);

    if (!p.goodId) {
      const draftGoodId = getNewId("good");
      const savedGoodId = await saveGood({
        id: draftGoodId,
        marketplace,
        contentType: storageContentType,
        versions: [
          {
            id: getNewId("version"),
            title: p.title,
            description: p.description,
            media: { images: imageUrls },
            activePeriods: [],
            isActive: false,
            contentType: storageContentType,
          },
        ],
        publishStatus: "draft",
        characteristics: sanitizeCharacteristics(p.characteristics),
        competitors: p.competitors,
        images: imageUrls,
        category: wizardCategoryForGood(data.value.generalCategory),
        brand: p.brand ?? "",
        publicationData: { wb: {} },
      } as Good);
      p.goodId = String(savedGoodId ?? draftGoodId).trim() || draftGoodId;
    } else {
      try {
        await patchDraftGoodMetadata(p.goodId, {
          contentType: storageContentType,
          marketplace: marketplaceKeyToApi(marketplace),
          title: p.title,
          images: imageUrls.length ? imageUrls : undefined,
        });
      } catch (error) {
        console.warn("[ensureDraftGoodSaved] patch metadata failed", error);
      }
    }

    syncWizardDraftInGoodsStore(p.goodId);
    return p.goodId;
  }

  /** Нормализует поля шага 1 и сохраняет черновик товара перед переходом на шаг 2. */
  async function prepareForContentGeneration(): Promise<void> {
    ensureWizardStep1ProductFields();
    await ensureDraftGoodSaved();
  }

  async function uploadGeneratedImageVariant(url: string): Promise<string> {
    const uploadedRef = await uploadImageToCrm(url);
    return resolveImageUrl(uploadedRef);
  }

  /**
   * Передаёт активную content-job фоновому вотчеру и просит мастер прекратить её вести
   * (без снятия DB-маркера). Вызывается, когда юзер уходит с экрана генерации «Назад».
   * Так у джобы остаётся ровно один владелец стрима, а товар продолжает показываться как
   * «Генерируется» в списке и доводится до конца вотчером (в т.ч. после reload).
   */
  function detachActiveContentJobToWatcher(): void {
    const job = activeContentJob.value;
    const goodId = String(data.value.product.goodId ?? "").trim();
    if (!job || !goodId) return;
    detachRequested.value = true;
    if (contentJobUnsubscribe) {
      contentJobUnsubscribe();
      contentJobUnsubscribe = null;
    }
    void import("./contentJobWatcher").then(({ useContentJobWatcher }) => {
      useContentJobWatcher().watchJob(
        { goodId, contentJobId: job.jobId, contentJobOwnerId: job.ownerId },
        { force: true },
      );
    });
  }

  async function runActiveContentJob(): Promise<void> {
    const job = activeContentJob.value;
    if (!job) return;

    const p0 = data.value.product;
    const isImagesOnlyFlow = data.value.contentType === "photo";
    const pipelineSourceImages =
      pipelineSourceCatalog.value.length > 0
        ? pipelineSourceCatalog.value
        : p0.images.map((img) => ({
            id: img.id,
            url: resolveImageUrl(String(img.url ?? "")),
          }));
    const fallbackSource = pipelineSourceImages[0];

    pipelineInFlight.value = true;
    lastContentPipelineSucceeded.value = false;
    detachRequested.value = false;
    improvePhase.value = "editor";
    data.value.product.generatingTitle = !isImagesOnlyFlow;
    data.value.product.generatingDescription = !isImagesOnlyFlow;
    const pendingQuickTargets = new Set(pendingPhotoQuickEdits.value.map((edit) => edit.targetImageId));
    for (const ph of p0.images) {
      if (pendingQuickTargets.has(ph.id)) continue;
      if ((ph.variants?.length ?? 0) === 0) {
        ph.generating = true;
      }
    }

    try {
      const { jobId, ownerId } = job;

      const handleEvent = (event: ContentJobEvent) => {
        if (event.type === "content.job.progress") {
          const stage = event.payload.stage;
          preparationProgress.value = Number(event.payload.progressPercent ?? 0);
          preparationStageIndex.value = stage === "planning" ? 0 : stage === "text_generation" ? 1 : stage === "image_generation" ? 2 : 3;
          void persistWizardState();
          return;
        }
        if (event.type === "content.job.title.generated" && !isImagesOnlyFlow) {
          const nextTitle = event.payload.title;
          if (typeof nextTitle === "string" && nextTitle.trim()) {
            p0.aiTitles = [{ id: getNewId("title"), text: nextTitle, source: "ai" }];
            p0.title = nextTitle;
            p0.selectedAiTitleId = p0.aiTitles[0].id;
            p0.generatingTitle = false;
          }
          void persistWizardState();
          return;
        }
        if (event.type === "content.job.description.generated" && !isImagesOnlyFlow) {
          const nextDescription = event.payload.description;
          if (typeof nextDescription === "string" && nextDescription.trim()) {
            p0.aiDescriptions = [{ id: getNewId("desc"), text: nextDescription, source: "ai" }];
            p0.description = nextDescription;
            p0.selectedAiDescriptionId = p0.aiDescriptions[0].id;
            p0.generatingDescription = false;
          }
          void persistWizardState();
          return;
        }
        if (event.type === "content.job.image.generated") {
          const rawSlotIndex = Number(event.payload.slotIndex ?? 0);
          const imageUrl = event.payload.url;
          if (typeof imageUrl !== "string" || !imageUrl) return;
          // GC-36: оригиналы лежат в p0.images[0..originalsCount-1] (variants[0].source === "user").
          // AI-слоты идут после них: реальный индекс = rawSlotIndex + originalsCount.
          const originalsCount = p0.images.filter((img) => img.variants?.[0]?.source === "user").length;
          const slotIndex = rawSlotIndex + originalsCount;
          // Если placeholder под slotIndex ещё не создан — создаём его (и все
          // промежуточные). Это покрывает text-only flow (юзер не грузил
          // свои фото на шаге 1) и resume после reload.
          while (p0.images.length <= slotIndex) {
            p0.images.push({ id: getNewId("image"), url: "", variants: [] });
          }
          const target = p0.images[slotIndex];
          if (pendingQuickTargets.has(target.id)) return;
          const variantId = event.payload.variantId || getNewId("image");
          const source = pipelineSourceImages[rawSlotIndex % Math.max(1, pipelineSourceImages.length)] ?? fallbackSource;
          target.url = source?.url ?? target.url;
          // GC-36: variants дополняем, не перезаписываем, чтобы AI-результат сохранился рядом с возможным user-вариантом.
          target.variants.push({ id: variantId, url: imageUrl, source: "ai:pipeline" });
          // GC-59: pickedVariantIndex — индекс в виртуальном массиве [image.url, ...variants],
          // где 0 = плейсхолдер-оригинал. Значит реальный AI-вариант — на позиции variants.length.
          target.pickedVariantIndex = target.variants.length;
          target.selectedVariantId = variantId;
          target.generating = false;
          void persistWizardState();
        }
      };

      let isTerminalEventReceived = false;
      let isFailedByEvent = false;
      let failedByEventMessage = "Генерация завершилась с ошибкой";

      if (contentJobUnsubscribe) {
        contentJobUnsubscribe();
        contentJobUnsubscribe = null;
      }

      contentJobUnsubscribe = subscribeContentJobEvents(ownerId, jobId, {
        onEvent: (event: ContentJobEvent) => {
          handleEvent(event);
          if (event.type === "content.job.completed") {
            isTerminalEventReceived = true;
          }
          if (event.type === "content.job.failed") {
            isTerminalEventReceived = true;
            isFailedByEvent = true;
            failedByEventMessage = typeof event.payload.message === "string" && event.payload.message.trim() ? event.payload.message : failedByEventMessage;
          }
        },
        onError: async () => {
          try {
            const snapshot = await getContentJobSnapshot(ownerId, jobId);
            preparationProgress.value = snapshot.progressPercent ?? preparationProgress.value;
            if (!isImagesOnlyFlow && snapshot.partial.title) {
              p0.aiTitles = [{ id: getNewId("title"), text: snapshot.partial.title, source: "ai" }];
              p0.title = snapshot.partial.title;
              p0.selectedAiTitleId = p0.aiTitles[0].id;
              p0.generatingTitle = false;
            }
            if (!isImagesOnlyFlow && snapshot.partial.description) {
              p0.aiDescriptions = [{ id: getNewId("desc"), text: snapshot.partial.description, source: "ai" }];
              p0.description = snapshot.partial.description;
              p0.selectedAiDescriptionId = p0.aiDescriptions[0].id;
              p0.generatingDescription = false;
            }
            for (const row of snapshot.partial.images || []) {
              handleEvent({
                type: "content.job.image.generated",
                payload: {
                  jobId,
                  slotIndex: row.slotIndex,
                  url: row.url,
                  variantId: row.variantId,
                },
              });
            }
          } catch (error) {
            console.error("[runActiveContentJob] failed to restore job snapshot", error);
          }
        },
      });

      const deadline = Date.now() + 8 * 60 * 1000;
      let latestSnapshot = await getContentJobSnapshot(ownerId, jobId);
      handleEventFromSnapshot(latestSnapshot, jobId, handleEvent, isImagesOnlyFlow);
      if (latestSnapshot.status === "completed") {
        isTerminalEventReceived = true;
      }
      if (latestSnapshot.status === "failed") {
        throw new Error("Генерация завершилась с ошибкой на сервере");
      }

      while (!isTerminalEventReceived && !detachRequested.value && Date.now() < deadline) {
        latestSnapshot = await getContentJobSnapshot(ownerId, jobId);
        preparationProgress.value = latestSnapshot.progressPercent ?? preparationProgress.value;
        handleEventFromSnapshot(latestSnapshot, jobId, handleEvent, isImagesOnlyFlow);
        if (latestSnapshot.status === "completed") {
          isTerminalEventReceived = true;
          break;
        }
        if (latestSnapshot.status === "failed") {
          throw new Error("Генерация завершилась с ошибкой на сервере");
        }
        await new Promise((resolve) => setTimeout(resolve, 2500));
      }

      // Юзер ушёл с экрана генерации — джобу теперь ведёт фоновый вотчер. НЕ финализируем
      // здесь (иначе двойной тост/refresh): просто выходим, finally снимет локальное
      // состояние мастера. DB-маркер при этом НЕ снимаем — товар остаётся «Генерируется».
      if (detachRequested.value) {
        return;
      }

      if (isFailedByEvent) {
        throw new Error(failedByEventMessage);
      }
      if (!isTerminalEventReceived) {
        throw new Error("Превышено время ожидания генерации контента");
      }

      await finalizeContentPipelineSuccess(isImagesOnlyFlow, p0);
    } catch (e) {
      console.error("[runActiveContentJob]", e);
      lastContentPipelineSucceeded.value = false;
      strategyBundles.value = [];
      data.value.product.aiTitles = [];
      data.value.product.aiDescriptions = [];
      deepAnalysisCacheKey.value = computeProductKey();
      preparationProgress.value = 100;
      improvePhase.value = "editor";
    } finally {
      if (contentJobUnsubscribe) {
        contentJobUnsubscribe();
        contentJobUnsubscribe = null;
      }
      for (const ph of data.value.product.images) {
        ph.generating = false;
      }
      data.value.product.generatingTitle = false;
      data.value.product.generatingDescription = false;
      pipelineInFlight.value = false;
      activeContentJob.value = null;
      void persistWizardState();
    }
  }

  function handleEventFromSnapshot(
    snapshot: Awaited<ReturnType<typeof getContentJobSnapshot>>,
    jobId: string,
    handleEvent: (event: ContentJobEvent) => void,
    isImagesOnlyFlow: boolean
  ) {
    const p0 = data.value.product;
    if (!isImagesOnlyFlow && snapshot.partial.title) {
      handleEvent({
        type: "content.job.title.generated",
        payload: { jobId, title: snapshot.partial.title },
      });
    }
    if (!isImagesOnlyFlow && snapshot.partial.description) {
      handleEvent({
        type: "content.job.description.generated",
        payload: { jobId, description: snapshot.partial.description },
      });
    }
    for (const row of snapshot.partial.images || []) {
      handleEvent({
        type: "content.job.image.generated",
        payload: {
          jobId,
          slotIndex: row.slotIndex,
          url: row.url,
          variantId: row.variantId,
        },
      });
    }
  }

  async function finalizeContentPipelineSuccess(
    isImagesOnlyFlow: boolean,
    p0: (typeof data.value)["product"]
  ): Promise<void> {
    const targetPipelineImageCount = Math.max(1, p0.images.length);
    let pipelineImagesCount = p0.images.filter((img) => img.variants?.some((v) => v.source === "ai:pipeline")).length;
    if (pipelineImagesCount === 0) {
      pipelineImagesCount = p0.images.filter((img) => !img.generating && (img.variants?.length ?? 0) > 0).length;
    }
    if (pipelineImagesCount === 0) {
      pipelineImagesCount = targetPipelineImageCount;
    }
    void recordGenerationStat({
      sessionKind: isImagesOnlyFlow ? "card_photo_only" : "card_full",
      imagesGenerated: pipelineImagesCount,
      textsGenerated: isImagesOnlyFlow ? 0 : 2,
    }).catch(() => {});

    if (!isImagesOnlyFlow && p0.title && p0.description) {
      strategyBundles.value = [
        {
          id: getNewId("strategy"),
          strategyTitle: "Базовая стратегия",
          metricImpact: "",
          keyImprovement: "",
          title: p0.title,
          description: p0.description,
          summary: "Generated by content job pipeline",
        },
      ];
      selectedStrategyIndex.value = 0;
      applyStrategyFromIndex(0);
    }

    deepAnalysisCacheKey.value = computeProductKey();
    preparationProgress.value = 100;
    improvePhase.value = "editor";
    lastContentPipelineSucceeded.value = true;
    activeContentJob.value = null;

    // Снять маркер «генерируется» в pinia + БД, подтянуть сохранённую версию и показать
    // тост. Биллинг/статистика остаются выше (recordGenerationStat) — это забота мастера.
    const finalizedGoodId = String(p0.goodId ?? "").trim();
    if (finalizedGoodId) {
      void markGoodGenerationComplete(finalizedGoodId);
    }
  }

  /** Full marketplace pipeline: image plan → best text strategy → images + compliance. */
  async function startImprovementPipeline() {
    if (activeContentJob.value) {
      await runActiveContentJob();
      return;
    }
    if (data.value.contentType === "video") {
      // Video flow uses a separate (mock) generation path. Keep wizard responsive.
      improvePhase.value = "editor";
      preparationProgress.value = 100;
      preparationStageIndex.value = 0;
      data.value.product.generatingTitle = false;
      data.value.product.generatingDescription = false;
      if (!data.value.product.videoUrl) {
        data.value.product.videoUrl = "/cards.mp4";
      }
      return;
    }
    if (pipelineInFlight.value) return;
    pipelineInFlight.value = true;
    lastContentPipelineSucceeded.value = false;
    improvePhase.value = "pipeline";
    preparationProgress.value = 5;
    preparationStageIndex.value = 0;
    data.value.product.generatingTitle = true;
    data.value.product.generatingDescription = true;

    const p0 = data.value.product;
    const isImagesOnlyFlow = data.value.contentType === "photo";
    if (!p0.originalProductTitle) {
      p0.originalProductTitle = p0.title;
      p0.originalProductDescription = p0.description;
    }

    try {
      ensureWizardStep1ProductFields();
      await ensureDraftGoodSaved();

      const marketplace = marketplaceKeyToApi(resolveWizardMarketplace());
      preparationProgress.value = 10;
      await new Promise((resolve) => setTimeout(resolve, 350));
      for (const ph of p0.images) {
        ph.generating = true;
      }
      improvePhase.value = "editor";

      // Backend pipeline cannot access browser-local blob URLs; upload them first.
      for (const image of p0.images) {
        if (image.url?.startsWith("blob:") || image.url?.startsWith("data:")) {
          try {
            const uploadedRef = await uploadImageToCrm(image.url);
            image.url = resolveImageUrl(uploadedRef);
          } catch (error) {
            console.error("[startImprovementPipeline] failed to upload source image", error);
            throw new Error("Не удалось загрузить исходное изображение на сервер");
          }
        }
        if (!/^https?:\/\//i.test(String(image.url ?? ""))) {
          image.url = resolveImageUrl(String(image.url ?? ""));
        }
      }

      const pipelineSourceImages = p0.images.map((img) => ({
        id: img.id,
        url: resolveImageUrl(String(img.url ?? "")),
      }));
      pipelineSourceCatalog.value = pipelineSourceImages.map((img) => ({ id: img.id, url: img.url }));
      const fallbackSource = pipelineSourceImages[0];
      const sourceImageCount = pipelineSourceImages.length;
      // GC-36: pipeline просит ровно столько AI-картинок, сколько загружено оригиналов
      // (1 source → 1 generation, без принудительного второго фото).
      const targetPipelineImageCount = Math.max(
        1,
        Math.min(MAX_PIPELINE_IMAGE_COUNT, sourceImageCount),
      );
      if (fallbackSource) {
        // GC-36: оригиналы юзера остаются в p0.images как первые N слотов с variants=[{source:"user"}].
        // AI-генерации добавляются ПОСЛЕ оригиналов отдельными слотами. Обработчики content.job.image.generated
        // используют offset = число оригиналов (см. внизу — динамически считается).
        const originalImages = pipelineSourceImages.map((src, idx) => {
          const existing = p0.images[idx];
          const userVariantId = existing?.variants?.[0]?.id ?? getNewId("image");
          return {
            id: existing?.id ?? getNewId("image"),
            url: src.url,
            variants: [{ id: userVariantId, url: src.url, source: "user" as const }] as VariantItem[],
            pickedVariantIndex: 0,
            selectedVariantId: userVariantId,
            generating: false,
          };
        });
        const aiSlots = Array.from({ length: targetPipelineImageCount }, (_, idx) => {
          const source = pipelineSourceImages[idx % Math.max(1, pipelineSourceImages.length)] ?? fallbackSource;
          return {
            id: getNewId("image"),
            url: source.url, // плейсхолдер до получения AI-результата
            variants: [] as VariantItem[],
            pickedVariantIndex: 0,
            selectedVariantId: undefined,
            generating: true,
            // GC-21+GC-36: маркер «слот принадлежит pipeline». Нужен чтобы
            // reconcileOrphanedPhotoQuickEdits после F5 не воспринимал этот
            // слот как «duplicate slot pattern» (URL совпадает с оригиналом)
            // и не запускал лишний enhanceQuality, повторно списывая geni.
            pipelineSlot: true,
          };
        });
        p0.images = [...originalImages, ...aiSlots];
      }
      await persistWizardState();

      const authStore = useAuthStore();
      const ownerId = String(authStore.cardGeniusAccountProfile?.userId ?? "");
      if (!ownerId) {
        throw new Error("Authenticated userId is required to start content job");
      }
      ensureWizardStep1ProductFields();
      const goodId = String(p0.goodId ?? "").trim();
      const productTitle = String(p0.title ?? "").trim() || "Товар";
      const productDescription =
        String(p0.description ?? "").trim() ||
        productTitle ||
        String(data.value.generationNotes ?? "").trim() ||
        String(data.value.generalCategory ?? "").trim() ||
        "Описание будет сгенерировано автоматически";

      // Generate the job id on the CLIENT and persist it BEFORE sending the request.
      // If the user refreshes mid-generation, the persisted jobId lets the app resume
      // this same job instead of starting a brand-new one — which previously caused a
      // duplicate generation and a second GENI charge (BUG-001/002).
      const jobId = randomUUID();
      activeContentJob.value = { jobId, ownerId };
      await persistWizardState();

      const resolvedGoodId = goodId || (await ensureDraftGoodSaved());

      await createContentJob({
        jobId,
        ownerId,
        goodId: resolvedGoodId,
        versionId: p0.versionId || undefined,
        marketplace,
        contentType: resolveWizardContentJobContentType(data.value.contentType),
        productTitle,
        productDescription,
        generationNotes: data.value.generationNotes,
        categoryLabel: data.value.generalCategory,
        activePeriods: p0.activePeriods,
        characteristics: p0.characteristics,
        competitors: p0.competitors as Array<Record<string, unknown>>,
        sourceImages: pipelineSourceImages,
        desiredImageCount: targetPipelineImageCount,
      });

      // Помечаем товар как «генерируется» в БД (маркер переживает reload / новую вкладку)
      // и сразу в pinia — чтобы он появился в списке со статусом «Генерируется» без
      // перезагрузки. Контекст пригодится для повторной отрисовки экрана после reload.
      p0.goodId = resolvedGoodId;
      try {
        await patchGoodGenerationMarker(resolvedGoodId, {
          generating: true,
          contentJobId: jobId,
          contentJobOwnerId: ownerId,
          originalTitle: p0.originalProductTitle ?? p0.title,
          generationContext: {
            contentType: (data.value.contentType ?? undefined) as
              | "card"
              | "photo"
              | "video"
              | undefined,
            generationNotes: data.value.generationNotes,
            generalCategory: data.value.generalCategory,
            desiredImageCount: targetPipelineImageCount,
          },
        });
      } catch (markerError) {
        console.warn("[startImprovementPipeline] failed to write generation marker", markerError);
      }
      syncWizardDraftInGoodsStore(resolvedGoodId);

      await runActiveContentJob();
    } catch (e) {
      console.error("[startImprovementPipeline]", e);
      lastContentPipelineSucceeded.value = false;
      strategyBundles.value = [];
      data.value.product.aiTitles = [];
      data.value.product.aiDescriptions = [];
      deepAnalysisCacheKey.value = computeProductKey();
      preparationProgress.value = 100;
      improvePhase.value = "editor";
    } finally {
      if (contentJobUnsubscribe) {
        contentJobUnsubscribe();
        contentJobUnsubscribe = null;
      }
      for (const ph of data.value.product.images) {
        ph.generating = false;
      }
      data.value.product.generatingTitle = false;
      data.value.product.generatingDescription = false;
      pipelineInFlight.value = false;
    }
  }

  async function getAIVariants() {
    await startImprovementPipeline();
  }

  async function generateVariant(kind: "title" | "description") {
    const idx = selectedStrategyIndex.value;
    const taskId = getNewId("text_task");
    registerPendingTextTask({ taskId, mode: "variant", field: kind, strategyIndex: idx });
    await executePendingTextTask({
      taskId,
      mode: "variant",
      field: kind,
      strategyIndex: idx,
    });
  }

  async function generateTextByIdea(userText: string) {
    const scope = aiAssistentChatModal.value.scope;
    if (scope !== "title" && scope !== "description") return;
    aiAssistentChatModal.value.isGenerating = true;
    const idx = selectedStrategyIndex.value;
    const taskId = getNewId("text_task");
    registerPendingTextTask({
      taskId,
      mode: "refine",
      field: scope,
      userText,
      strategyIndex: idx,
    });
    try {
      await executePendingTextTask({
        taskId,
        mode: "refine",
        field: scope,
        userText,
        strategyIndex: idx,
      });
      const p = data.value.product;
      const next = scope === "title" ? p.title : p.description;
      const label = scope === "title" ? "Новый заголовок" : "Новое описание";
      aiAssistentChatModal.value.messages.push({
        role: "assistant",
        content: `Готово — текст обновлён по вашей инструкции. ${label}:`,
        resultBlock: { label, text: next },
      });
      aiAssistentGenerationFinished({
        scope,
        outcome: "success",
        resultType: "text",
        promptLength: userText.length,
      });
    } catch (error) {
      console.error(error);
      aiAssistentGenerationFinished({
        scope,
        outcome: "error",
        resultType: "text",
        promptLength: userText.length,
        errorKind: error instanceof Error ? error.name : "unknown",
      });
    } finally {
      aiAssistentChatModal.value.isGenerating = false;
    }
  }

  function moveImageToCover(photoId: string) {
    if (data.value.contentType === "photo") return;
    const images = data.value.product.images;
    const idx = images.findIndex((p) => p.id === photoId);
    if (idx <= 0) return;
    const [item] = images.splice(idx, 1);
    images.unshift(item);
  }

  function removeProductImage(photoId: string) {
    const images = data.value.product.images;
    if (images.length <= 1) return;
    const idx = images.findIndex((p) => p.id === photoId);
    if (idx < 0) return;
    images.splice(idx, 1);
  }

  /**
   * Вставить справа от исходного фото копию с тем же превью и флагом generating —
   * для UX «новое фото», пока идёт AI-обработка, не трогая исходную плитку.
   */
  function insertProductImageDuplicateAfter(photoId: string, resolvedDisplayUrl: string): string | null {
    const images = data.value.product.images;
    if (images.length >= MAX_PRODUCT_GRID_IMAGES) return null;
    const idx = images.findIndex((p) => p.id === photoId);
    if (idx < 0) return null;
    const id = getNewId("image");
    images.splice(idx + 1, 0, {
      id,
      url: resolvedDisplayUrl,
      variants: [],
      pickedVariantIndex: 0,
      selectedVariantId: undefined,
      generating: true,
    });
    flushWizardPersistence();
    return id;
  }

  /** Добавить фото в сетку редактора (URL уже нормализован или будет пропущен через resolveImageUrl). */
  function appendProductImageFromResolvedUrl(url: string): string | null {
    if (data.value.product.images.length >= MAX_PRODUCT_GRID_IMAGES) return null;
    const id = getNewId("image");
    data.value.product.images.push({
      id,
      url: resolveImageUrl(url),
      variants: [],
      pickedVariantIndex: 0,
      selectedVariantId: undefined,
      generating: false,
    });
    return id;
  }

  function setGoodVersion(goodVersion: GoodVersion, good: Good) {
    clearImprovementSession();
    data.value.product = {
      goodId: good.id,
      versionId: goodVersion.id,
      title: goodVersion.title,
      description: goodVersion.description,
      price: 0,
      images: goodVersion.media.images.map((url) => ({ url: resolveImageUrl(url), variants: [], id: getNewId("image") })),
      characteristics: good.characteristics,
      aiTitles: [],
      aiDescriptions: [],
      selectedAiTitleId: null,
      selectedAiDescriptionId: null,
      generatingTitle: false,
      generatingDescription: false,
      competitors: good.competitors,
      savingVersion: false,
      isActive: goodVersion.isActive,
      category: good.category,
      brand: good.brand,
      characteristicsSchema: good.publicationData.wb.characteristics,
      activePeriods: goodVersion.activePeriods,
      originalProductTitle: goodVersion.title,
      originalProductDescription: goodVersion.description,
    };
    data.value.contentType = good.contentType === "images" ? "photo" : good.contentType === "video" ? "video" : "card";
    data.value.productSourceType = "url";
    data.value.generationNotes = "";
    data.value.generalCategory = "";
    pipelineSourceCatalog.value = [];
  }

  async function saveVersion() {
    // Finalize the current data (uploading/creating on server happens here).
    // console.log(data.value);

    if (!data.value.product.versionId) {
      data.value.product.versionId = getNewId("version");
    }

    const images = [];

    for (const imageObject of data.value.product.images) {
      let image: Blob | string | null = null;

      const selectedVariant = imageObject.variants.find((v) => v.id === imageObject.selectedVariantId);

      if (!selectedVariant) {
        image = imageObject.url;
      } else {
        image = selectedVariant.url;
      }

      const imageUrl = await uploadImageToCrm(image);

      images.push(imageUrl);
    }

    // console.log(images);

    const versionObject = sanitizeGoodVersionForApi({
      id: data.value.product.versionId,
      title: data.value.product.title,
      description: data.value.product.description,
      contentType: resolveWizardGoodStorageContentType(data.value.contentType),
      createdAt: new Date(),
      media: {
        images: images,
        video: data.value.contentType === "video" ? data.value.product.videoUrl || "/cards.mp4" : undefined,
      },
      activePeriods: data.value.product.activePeriods,
      isActive: data.value.product.isActive,
    });

    const profile_id = await saveGoodVersion(versionObject, data.value.product.goodId);

    versionObject.profile_id = profile_id;

    // Revoke any transient object URLs after finalizing to avoid leaks
    revokeBlobUrls();

    return versionObject;
  }

  function resolveNewWizardMarketplace(marketplaceOverride?: MarketplaceKey | null): MarketplaceKey {
    if (marketplaceOverride) return marketplaceOverride;
    const authStore = useAuthStore();
    return (authStore.preferredMarketplace ?? "wb") as MarketplaceKey;
  }

  function openNewCardWizard(marketplaceOverride?: MarketplaceKey | null) {
    void clearWizardPersistence();
    clearManualStepDraftExtras();
    wizardSessionId.value = createWizardSessionId();
    activeContentJob.value = null;
    pendingPhotoQuickEdits.value = [];
    pendingTextTasks.value = [];
    pendingRegenerateAllPhotoIds.value = [];
    revokeBlobUrls();
    clearImprovementSession();
    manualStepBackLocked.value = false;
    history.value = [];
    data.value = {
      marketplace: resolveNewWizardMarketplace(marketplaceOverride),
      method: "manual",
      contentType: null,
      productSourceType: null,
      generationNotes: "",
      generalCategory: "",
      product: {
        goodId: "",
        versionId: "",
        title: "",
        description: "",
        price: null,
        images: [],
        videoUrl: null,
        characteristics: [],
        aiTitles: [],
        aiDescriptions: [],
        selectedAiTitleId: null,
        selectedAiDescriptionId: null,
        generatingTitle: false,
        generatingDescription: false,
        competitors: [],
        savingVersion: false,
        isActive: false,
        category: null,
        brand: null,
        characteristicsSchema: null,
        activePeriods: [],
        originalProductTitle: null,
        originalProductDescription: null,
      },
    };
    step.value = "editGoodInfoManually";
    pipelineSourceCatalog.value = [];
  }

  function reset() {
    void clearWizardPersistence();
    clearManualStepDraftExtras();
    activeContentJob.value = null;
    pendingPhotoQuickEdits.value = [];
    pendingTextTasks.value = [];
    pendingRegenerateAllPhotoIds.value = [];
    // revoke any created object URLs when resetting the wizard to avoid leaks
    revokeBlobUrls();
    clearImprovementSession();
    manualStepBackLocked.value = false;

    step.value = "editGoodInfoManually";
    history.value = [];
    pipelineSourceCatalog.value = [];
    data.value = {
      marketplace: null,
      method: null,
      contentType: null,
      productSourceType: null,
      generationNotes: "",
      generalCategory: "",
      product: {
        goodId: "",
        versionId: "",
        title: "",
        description: "",
        price: null,
        images: [],
        videoUrl: null,
        characteristics: [],
        aiTitles: [],
        aiDescriptions: [],
        selectedAiTitleId: null,
        selectedAiDescriptionId: null,
        generatingTitle: false,
        generatingDescription: false,
        competitors: [],
        savingVersion: false,
        isActive: false,
        category: null,
        brand: null,
        characteristicsSchema: null,
        activePeriods: [],
        originalProductTitle: null,
        originalProductDescription: null,
      },
    };
  }

  watch(open, (isOpen, wasOpen) => {
    if (isOpen && !wasOpen && wizardPhaseFromStep(step.value) != null) {
      contentCreationWizardOpened({
        wizardStep: step.value,
        marketplace: data.value.marketplace,
        content_type: data.value.contentType,
        product_source_type: data.value.productSourceType,
      });
    }
  });

  watch(
    () => [data.value.marketplace, data.value.contentType, data.value.productSourceType] as const,
    ([marketplace, content_type, product_source_type]) => {
      syncContentCreationWizardContext({
        marketplace,
        content_type,
        product_source_type,
      });
    }
  );

  watch(improvePhase, (phase) => {
    updateContentCreationImprovePhase(phase);
  });

  watch(open, (isOpen) => {
    if (isOpen) {
      if (!wizardSessionId.value) {
        wizardSessionId.value = createWizardSessionId();
      }
      schedulePersistWizardState();
    }
  });

  watch(
    [
      step,
      history,
      improvePhase,
      manualStepBackLocked,
      preparationProgress,
      preparationStageIndex,
      strategyBundles,
      selectedStrategyIndex,
      deepAnalysisCacheKey,
      lastContentPipelineSucceeded,
      waitingForGoodsSync,
      pipelineInFlight,
      pipelineSourceCatalog,
      activeContentJob,
      pendingPhotoQuickEdits,
      pendingTextTasks,
      pendingRegenerateAllPhotoIds,
      manualStepUrlSelectedIds,
      pendingPhotoAnalysis,
      pendingUrlImport,
      manualStepImportUrl,
    ],
    () => schedulePersistWizardState(),
    { deep: true }
  );

  watch(data, () => schedulePersistWizardState(), { deep: true });

  // ── Persist content-job sources to IndexedDB ─────────────────────────────
  // Пока есть активный AI-pipeline, сохраняем мапинг slotIndex → sourceUrl и
  // выбранный variantId по jobId. Это позволяет при F5 на странице AI
  // (`/goods/new/ai?job=...`) сразу показать source-картинки и подсветить
  // выбор юзера, пока snapshot/SSE догружают AI-варианты.
  let contentJobSourcesWriteTimer: ReturnType<typeof setTimeout> | null = null;
  watch(
    [
      () => activeContentJob.value?.jobId ?? null,
      () =>
        data.value.product.images.map((i) => ({
          id: i.id,
          url: i.url,
          selectedVariantId: i.selectedVariantId,
        })),
    ],
    ([jobIdNow]) => {
      if (!jobIdNow) return;
      if (data.value.product.images.length === 0) return; // не затираем полезную запись пустотой
      if (contentJobSourcesWriteTimer) clearTimeout(contentJobSourcesWriteTimer);
      contentJobSourcesWriteTimer = setTimeout(() => {
        const job = activeContentJob.value;
        if (!job) return;
        void putContentJobSources({
          jobId: job.jobId,
          ownerId: job.ownerId,
          slots: data.value.product.images.map((img, idx) => ({
            slotIndex: idx,
            imageId: img.id,
            sourceUrl: img.url ?? "",
            selectedVariantId: img.selectedVariantId,
          })),
          savedAt: Date.now(),
        });
      }, 300);
    },
    { deep: true, immediate: true },
  );

  return {
    open,
    step,
    history,
    data,
    titles,
    manualStepBackLocked,
    reset,
    openNewCardWizard,
    restoreContentCreationWizard,
    isWizardUiEmpty,
    hasStep1ContentInMemory,
    reconcileEditorGenerationUi,
    registerWizardPhotoFile,
    getWizardPhotoBlob,
    registerPendingPhotoQuickEdit,
    clearPendingPhotoQuickEdit,
    runProductPhotoQuickEdit,
    registerPendingRegenerateAll,
    removePendingRegenerateAllPhoto,
    clearPendingRegenerateAll,
    flushWizardPersistence,
    forcePersistWizardState,
    clearWizardPersistence,
    manualStepUrlSelectedIds,
    pendingPhotoAnalysis,
    pendingUrlImport,
    manualStepImportUrl,
    setPendingPhotoAnalysis,
    clearPendingPhotoAnalysis,
    setPendingUrlImport,
    clearPendingUrlImport,
    setManualStepImportUrl,
    clearManualStepDraftExtras,
    go,
    enter,
    back,
    saveVersion,
    pickImageVariant,
    setGoodVersion,

    improvePhase,
    preparationProgress,
    preparationStageIndex,
    strategyBundles,
    selectedStrategyIndex,
    deepAnalysisCacheKey,
    lastContentPipelineSucceeded,
    waitingForGoodsSync,

    pendingPhotoQuickEdits,
    pendingTextTasks,
    pendingRegenerateAllPhotoIds,
    activeContentJob,
    runActiveContentJob,
    detachActiveContentJobToWatcher,
    replayTerminalContentJobFromQuery,
    startImprovementPipeline,
    selectStrategy,
    applyStrategyFromIndex,
    clearImprovementSession,
    moveImageToCover,
    removeProductImage,
    insertProductImageDuplicateAfter,
    appendProductImageFromResolvedUrl,

    pipelineSourceCatalog,

    // Expose transient url tracking so components can register created blob URLs
    transientBlobUrls,
    registerBlobUrl,
    revokeBlobUrls,

    prepareForContentGeneration,
    getAIVariants,
    generateVariant,

    deleteImageVersionModal,
    deleteImageVersion,

    aiAssistentChatModal,
    generateImageByIdea,
    generateTextByIdea,
    openAIAssistentChat,
    openAIAssistentChatForProductPhoto,
    openAIAssistentChatForResolvedUrl,
    openAIAssistentChatForGallery,
    openTextAiChat,
    closeAIAssistentChat,
    getAiAssistentChatLivePreviewUrl,
    productImageIdeaSavedTick,
  };
});

// GC-6: Vite HMR без acceptHMRUpdate перезагружает только runtime, не
// определение стора — мои свежие правки в catch'ах generateImageByIdea
// не подхватывались без F5. С этой строкой Pinia корректно перезаписывает
// стор при HMR (см. https://pinia.vuejs.org/cookbook/hot-module-replacement.html).
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useGoodAddingWizardStore, import.meta.hot));
}
